import React, { useEffect, useContext, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material'
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { APERCU_EDITO } from "./apercu"

import moduleMiniIcon from "../../../images/moduleMiniIcon.svg"
import closeIcon from '../../../images/closeIcon.svg';
import smallLogo from '../../../images/smallLogo.svg';

import UserContext from "../../../contexts/UserContext"
import LanguageContext from "../../../contexts/LanguageContext"

import { getCurrentUser } from "../../../utils/auth"
import { GLB_URL_THESCAN } from "../../../utils/APIS"

// L7 : « ecrire Vrai / Faux et non VRAI / FAUX ». Les capitales ne viennent pas
// d'un textTransform mais des contenus eux-memes.
//
// Volontairement limite aux quatre libelles nommes par la review, et non a une
// regle generale de mise en casse. Une regle generale abime les intitules reels :
// « STAY FOCUS ON B1 » deviendrait « Stay focus on b1 » et « SSC LC » « Ssc lc ».
// Les noms de modules et de chapitres relevent d'un renommage en base, pas d'une
// transformation a l'affichage (cf QUESTIONS-NAWAL.md, points 1 et 2).
const LIBELLES_NORMALISES = {
  "VRAI": "Vrai",
  "FAUX": "Faux",
  "TRUE": "True",
  "FALSE": "False",
}

export const casseNormale = (texte) => {
  if (typeof texte !== "string") return texte
  return LIBELLES_NORMALISES[texte.trim()] || texte
}

/* La variante d'un quiz (QCM / Vrai-Faux) vit dans `utils/quizVariant` : c'est la
   SOURCE UNIQUE, partagee par le gabarit de creation, le panneau d'edition et le
   lecteur. Ne pas en refaire une copie ici. */

/* ⛔ LOT G (18/09/2026) — GRAMMAIRE DE COMPOSITION, RELEVEE SUR LES NODES FIGMA.
 *
 * Un ECRAN DE CONTENU se compose de DEUX zones : une zone principale BLANCHE, et
 * une zone fonctionnelle basse TEINTEE qui porte le champ / les cartes, l'aide
 * clavier et le CTA. Sur LAB, la page etait peinte en #FAF8F6 partout : la teinte
 * reservee a la bande servait de fond general, donc la bande etait invisible.
 *
 * ⚠️ CE N'EST PAS UN REGLAGE GLOBAL, et c'est le piege a eviter.
 *
 * Colonne MESURE = ce que montre la frame, relevee au node. Colonne POSE = ce
 * que le code rend aujourd'hui. Les deux sont distinguees a dessein : un tableau
 * qui annonce une valeur que le code ne rend pas est pire que pas de tableau du
 * tout, parce qu'on le relit ensuite comme une preuve.
 *
 *   FORMAT                 MESURE Figma          MESURE sur LAB apres lot G
 *   Video .................. aucune bande ......... aucune                    =
 *   Texte (long text) ...... bande  111 px ........ 111 px, deux bords        =
 *   Texte + image .......... aucune bande ......... aucune                    =
 *   Input Text ............. bande  400 px ........ 415 px, deux bords        ~
 *   TextArea ............... bande  400 px ........ 415 px, deux bords        ~
 *   Questionnaire .......... bande  400 px ........  85 px, deux bords        ECART
 *   Quizz (QCM) ............ bande  444 px ........ 653 px, deux bords        ECART
 *   Vrai/Faux .............. bande  400 px ........ 661 px, deux bords        ECART
 *   Quizz + image .......... bande a DROITE ....... aucune                    ECART
 *   Titre .................. aucune bande ......... aucune                    =
 *   Diagnostic, Plan ....... page TEINTEE ......... page teintee              =
 *   Fin de module .......... page TEINTEE ......... page teintee              =
 *
 * ⚠️ LA COLONNE DE DROITE EST MESUREE SUR LES CAPTURES LAB, PAS DEDUITE. Les
 * pixels sont lus a 1512 x 982 au bord gauche de l'ecran ; « deux bords » veut
 * dire que la teinte touche x=0 et x=1511. Deux lignes de la version precedente
 * de ce tableau etaient fausses : elle annonçait « bande basse seule (85 px) »
 * pour le champ de saisie et la zone de texte, qui ont en realite leur bande de
 * 415 px — elle existait avant le lot, invisible sur une page de la meme teinte.
 *
 * ⛔ L'ECART DE HAUTEUR DU QUIZ EST LE PLUS VISIBLE, et il n'est pas corrige ici.
 * La planche fixe la zone haute a 463 px et la bande a 444 ; le code donne a la
 * zone haute la hauteur de son contenu et a la bande tout le reste, d'ou 653 px,
 * soit un depart 209 px trop haut. Ce n'est pas un reglage de valeur : c'est le
 * choix entre une composition a hauteurs fixes et une composition fluide, et il
 * se tranche avec Valentin, pas en passant.
 *
 * TROIS LARGEURS, et elles ne se confondent pas. Mesurees a l'identique sur les
 * trois ecrans de question (quiz 3:282231, questionnaire 3:241965, champ de
 * saisie 3:40519), donc c'est une regle et non un cas particulier :
 *   enonce de la question ....... 1000 px centre (x=256)  -> LARGEUR_QUESTION
 *   colonne interactive + CTA .... 750 px centre (x=381)  -> LARGEUR_CARTES
 *   colonne de lecture d'un texte  720 px centre          -> LARGEUR_LECTURE
 * Le CTA se cale sur le bord DROIT de la colonne interactive (x=1131 sur 1512),
 * pas sur le bord de l'ecran. « Harmoniser » ces trois largeurs deferait la
 * composition : l'enonce respire, les reponses se resserrent.
 *
 * ECART ASSUME (Questionnaire) : sa bande fait 400 px sur la planche et englobe
 * toute la zone de reponse. Le code ne lui donne que la bande basse de 85 px.
 * Le champ de saisie et la zone de texte, eux, ont bien leur bande : ils
 * l'avaient DEJA avant ce lot, mais une page de la meme teinte la rendait
 * invisible — c'est tout le defaut que ce lot corrige.
 *
 * ECART ASSUME (quiz illustre) : sa bande devrait aller jusqu'au bord, la
 * moitie de l'image restant blanche (node 4:559). Elle epouse la colonne, et la
 * barre du bas reste neutre sur ces ecrans. Y arriver demande de sortir la bande
 * du bloc defilant : c'est une restructuration, pas un reglage.
 *
 * ECART ASSUME (largeurs des zones de reponse) : le CTA est pose a sa position
 * mesuree (bord droit d'une colonne de 750 px, x=1131), mais les zones qu'il
 * valide n'ont pas encore leur largeur de planche — champ de saisie 420 / 540 px
 * contre 750 mesures, piste du questionnaire 630 px contre 892. Les deux
 * largeurs actuelles viennent d'arbitrages traces ailleurs dans ce fichier ; on
 * ne les defait pas en passant. Consequence visible : le CTA ne touche pas le
 * bord de sa zone.
 *
 * FORMAT ABSENT DU TABLEAU : `Feedback`, format historique remis en service, n'a
 * aucune frame. Il tombe cote TEINTE par le defaut de `fondDeLEcran`, ce qui est
 * son rendu d'avant ce lot — on ne lui invente pas une composition.
 *
 * ECART ASSUME (aides clavier) : la rangee de controles porte a gauche « 1-4
 * pour choisir » et « entree pour valider ». Aucun gestionnaire de clavier
 * n'existe dans le lecteur. On ne dessine pas une aide qui mentirait.
 *
 * ⛔ LE BLANC NE SE POSE PAS SUR LA RACINE DU FORMAT. Il est pose sur le
 * conteneur de page, selon le type d'ecran, et demande a `fondDeLEcran`.
 * ⚠️ La raison a change au lot H sans que la regle change : elle etait « ne pas
 * masquer le decor rendu en dessous », le decor a depuis ete retire faute
 * d'exister dans les planches. La regle tient pour une raison plus simple :
 * SEPT endroits rendent un format, et un fond pose sur le format devrait etre
 * repose sept fois.
 *
 * ⛔ Ce n'est pas la resurrection de l'ancien BottomBar : la bande est une regle
 * de layout du NOUVEAU design, et elle n'existe que sur les formats qui la
 * dessinent reellement. */
export const FOND_ECRAN_CONTENU = "#FFFFFF"
export const FOND_BANDE_BASSE = "#FAF8F6"

/* Largeur de la colonne des CARTES de reponse, et de la rangee de controles qui
   les suit. Relevee sur le node Figma 3:302259 : 750 px, centres sur une planche
   de 1512 (x=381). Volontairement distincte de `LARGEUR_LECTURE` (720 px), qui
   borne le TEXTE : les deux colonnes n'ont pas la meme largeur sur la planche,
   et les confondre decale les cartes de 15 px de chaque cote. */
/* ⛔ LA GOUTTIERE MOBILE EST UNE CONSTANTE, ET C'EST UNE LECON (21/09/2026).
   Le lot 1 l'a fait passer de 25 a 20 px dans le conteneur central, mais le
   debord « bord a bord » de l'image gardait ses 50 px et ses marges de -25 :
   353 + 50 = 403 sur une fenetre de 393, soit 5 px de trop de chaque cote.
   Defaut invisible aux tests, au build et aux bancs CSS ; trouve par le banc de
   captures reelles. Les deux valeurs derivent desormais d'UNE SEULE source :
   elles ne peuvent plus diverger. */
export const GOUTTIERE_MOBILE = 20

export const LARGEUR_CARTES = "750px"

/* Largeur de l'ENONCE d'une question, releve sur le node 3:282231 : le bloc de
   question est a x=256 et fait 1000 px sur une planche de 1512, donc centre et
   NETTEMENT plus large que la colonne des cartes (750 px) et que la colonne de
   lecture d'un texte long (720 px). Trois largeurs distinctes, et c'est
   intentionnel sur la planche : l'enonce respire, les reponses se resserrent.
   Ne pas « harmoniser » les trois : ce serait defaire la composition. */
export const LARGEUR_QUESTION = "1000px"

/* ⛔ LE FOND D'UN ECRAN SE DEMANDE ICI, ET NULLE PART AILLEURS.
   Le blanc a d'abord ete pose sur la racine de chaque format, ou il masquait le
   decor ; puis sur le conteneur de page de `Formation.js`, ou il ne suivait plus
   le format. Les deux fois, la meme erreur : traiter le fond comme une propriete
   d'un lieu. C'est une propriete du TYPE D'ECRAN, et SEPT endroits rendent un
   format : la page apprenant, la vue « comme un apprenant » de l'admin, et CINQ
   vignettes qui passent par `ScaleThumbForm` (barre laterale de l'editeur,
   apercu de l'editeur, deux dans le plan de cours, une au tableau de bord).
   Un test de liste les compte, pour que ce chiffre ne derive pas. Une liste
   recopiee dans l'un d'eux aurait diverge des cinq autres : c'est exactement ce
   qui est arrive, et la vue de validation du redesign affichait teinte ce que
   l'apprenant voyait blanc.
   ⚠️ Un type absent de la liste tombe cote TEINTE. C'est volontaire : les ecrans
   teintes (Diagnostic, Plan, Fin de module) sont le comportement historique, donc
   un type oublie garde l'ancien rendu au lieu d'en inventer un nouveau. */
const TYPES_ECRAN_BLANC = ["Titre", "Texte", "Video", "Quizz", "InputTexte", "QuestionnaireV2", "Questionnaire"]

export const fondDeLEcran = (format) =>
  TYPES_ECRAN_BLANC.includes(format?.type) ? FOND_ECRAN_CONTENU : FOND_BANDE_BASSE

/* ⛔ 04/10/2026 — ECRAN ILLUSTRE : L'IMAGE PART DU HAUT. Planches 04, 05, 06'
   (3:80848, 3:100933, 3:181470, 3:181434) : en Mobile l'image commence sous
   l'en-tete, sans marge ; en Tablet a 60 px (`pt-60` du bloc image). Aux grands
   paliers, 0 : les deux colonnes partent sous l'en-tete (4:560). C'etait deja
   le cas de la saisie et du texte ; pour le QUIZ illustre (branche
   `sansMargesLaterales`) c'est un changement du 04/10 : la pastille passe de
   243 a 195 a 1512 x 982, la valeur de la planche 10 (4:562). */
const RETRAIT_ILLUSTRE = ["0px", "60px", "0px", "0px"]

/* Un nombre (px) ou une longueur CSS ecrite telle quelle. */
const enLongueur = (v, def) => (typeof v === "string" ? v : `${v ?? def}px`)

/* ⛔ PD-23 (03/10/2026) — LA VALEUR DE LA PLANCHE A LA HAUTEUR DE LA PLANCHE.
   Un retrait de planche (120 px en Desktop, 3:302251) ne vaut que pour une fenetre au
   moins aussi haute qu'elle ; en dessous, il retombe pixel pour pixel jusqu'a
   l'ancienne valeur, jamais plus bas. Fixe, il cachait des reponses du quiz a
   1440x790 et sortait le bouton de la saisie sur telephone en paysage.
   `100cqh` = hauteur de la racine du format (`containerType:"size"`). */
export const retraitPlanche = (ancien, planche, hauteurPlanche) =>
  `clamp(${ancien}px, calc(100cqh - ${hauteurPlanche - planche}px), ${planche}px)`
/* Hauteurs des planches : Desktop 982, Tablet 1194, Mobile 796 (855 moins la
   barre d'etat de 59). */
export const HAUTEUR_PLANCHE = { mobile: 796, tablette: 1194, desktop: 982 }
/* Marge laterale de la zone haute des ecrans de question (`px-[40px]`,
   3:302251, 3:261985). */
export const MARGE_QUESTION = 40

/* `cote` vaut "gauche", "droite", ou rien. Il remplace l'ancien booleen
   `ferreAGauche`, devenu insuffisant du jour ou l'image peut se poser des deux
   cotes : il fallait un TROISIEME etat, pas un second booleen a cote du
   premier — deux booleens finissent toujours par se contredire. */
/* `pastilleAvecImage` (30/09/2026, point 4 de la fiche de Nawal) — DEUX CAS,
   et il faut bien les deux.
   SANS image : la planche centre tout sur elle-meme, la pastille de la PAGE est
   a sa place, et celle de la colonne ne sert qu'aux paliers empiles. C'est le
   comportement d'origine, et il ne bouge pas.
   AVEC image : la question vit dans la moitie droite, la pastille de la page se
   retrouve centree sur la PLANCHE (756), loin de son bloc — le grief de Nawal.
   La maquette la pose DANS la colonne : nodes 3:181376 (06'), 3:100872 (04) et
   4:562 (10), dont le centre tombe a 1101,5, celui de la moitie de contenu. Celle
   de la colonne prend alors le relais A TOUS LES PALIERS, et celle de la page
   s'efface. Les deux ne sont jamais visibles ensemble.
   ⛔ Un seul drapeau ne suffisait pas : essaye, mesure. En rendant la pastille
   de colonne visible partout sans distinguer les deux cas, elle devenait un
   element de plus dans la RANGEE des ecrans sans image et poussait le contenu
   de 63 px vers la droite (carte de texte de 256 a 319, bouton de 1001 a 1064).
   Releve avant/apres, sur 16 ecrans. */
/* `pastilleEnTete` (04/10/2026, planche 14) : un TROISIEME cas, sans image.
   Le Diagnostic pose sa pastille EN TETE DE SA COLONNE DE GAUCHE, ferree a
   gauche, a tous les paliers (4:1435 a x=96, 4:1609 a x=42, 4:1768 a x=22) :
   ni centree sur la planche comme les ecrans de texte, ni dans une moitie
   comme les ecrans illustres. La valeur est l'ecart sous la pastille, palier
   par palier (20 en Mobile, 30 ailleurs : 4:1767, 4:1434) — un TABLEAU, pas un
   booleen (sans tableau, l'ecart de la pastille de colonne s'applique). Il
   suppose `pastilleDansColonne`, qui efface la pastille de page. */
export const CenterContainer = ({ passRef, actFormation, actIndex, noPartInfos, onlyContent, cote, sansMargesLaterales, pastilleDansColonne, pastilleAvecImage, pastilleEnTete, retraitHaut, retraitBas, basEmpile = 0, children, ...reste }) => {
  /* Le `sx` ouvert a ete retire : pose par-dessus le raccourci responsive de
     MUI, il etait ecrase en silence. On le dit, plutot que de l'ignorer. */
  if (process.env.NODE_ENV !== "production" && ("sx" in reste || "padding" in reste)) {
    console.warn("CenterContainer : `sx`/`padding` ne sont pas acceptes — un longhand y est ecrase par le raccourci responsive. Utiliser `sansMargesLaterales`.")
  }

  const [actPart, setActPart] = useState(null)


  useEffect(() => {
    if (actFormation && actIndex && noPartInfos !== true) {
      let findPart = actFormation?.chapitre[actIndex?.chapitre]?.partie[actIndex?.partie]
      if (findPart) {
        setActPart(findPart)
      }
    }
  }, [actFormation, actIndex, noPartInfos])

  return (
    /* ⛔ POINT 6 DE LA REVUE APPRENANT (Nawal, 04/09) — ANCRAGE HAUT.
       Ce conteneur portait `margin: auto 0px`, qui le centre VERTICALEMENT dans
       la colonne. Consequence : la hauteur du titre dependait de la quantite de
       contenu en dessous, et le titre sautait a chaque « Suivant ». Mesure au
       navigateur avant correctif, sur un module reel : le titre passait de 126
       a 297 px selon l'ecran, soit 171 px de derive. Nawal en a releve 260 sur
       un autre module.
       Le Figma ancre le bloc EN HAUT, a hauteur fixe, et laisse le vide en bas.
       On prend donc toute la hauteur restante (`flex: 1`) et on empile depuis
       le haut. `minHeight: 0` est indispensable : sans lui, un enfant en
       `overflow: auto` refuse de retrecir dans un parent flex et deborde.
       `withFlexAndJustifyContent` n'etait passe que par la video, pour obtenir
       ce meme `flex: 1` — mais centre. La regle devient la meme partout, comme
       le demande la fiche, et le drapeau disparait. */
    <Box ref={passRef} sx={{
      /* ⛔ UN SEUL TABLEAU `padding`, JAMAIS UN LONGHAND PAR-DESSUS.
         Le lot H avait d'abord expose un `sx` et lui passait
         `{paddingLeft:0, paddingRight:0}`. C'etait un NO-OP, et mesure comme
         tel : MUI emet le raccourci responsive dans QUATRE blocs `@media`, que
         stylis place APRES la regle de base ou vivent les longhands. A
         specificite egale, la regle la plus tardive gagne. Banc : avec
         `paddingLeft:0` declare, `getComputedStyle` rendait toujours 170 px a
         1512, 40 px a 1024 et 25 px a 500. Tout le lot reposait dessus.
         La variation se decide donc DANS le tableau, la ou elle ne peut pas
         etre ecrasee par elle-meme. */
      /* ⚠️ GOUTTIERES RELUES DANS FIGMA (21/09/2026, lot 1 de l'audit).
         Mobile 25 -> 20 et Tablet 40 -> 42. Ce ne sont pas des reglages d'oeil :
           Mobile  : le bloc de contenu fait 353 sur 20 occurrences des planches,
                     et 393 - 2 x 20 = 353 A L'UNITE. Le code rendait 343.
           Tablet  : il fait 750 sur 21 occurrences, et 834 - 2 x 42 = 750.
                     Le code rendait 754.
           Desktop : les 170 px donnent 1172, et l'enonce plafonne a 1000 tombe
                     juste sur la planche. CONFORME, on n'y touche pas.
           Palier md (1130-1240) : aucune planche Figma. On ne deduit rien, la
                     valeur de 60 px reste telle quelle. FIGMA MANQUANT.
         Trois agents d'audit independants ont releve l'ecart Mobile chacun de
         leur cote : carte de texte, enonce, en-tete. */
      /* `retraitBas` (04/10/2026) : la marge basse des paliers sm a lg, pour un
         ecran dont la planche la donne (fin de module : 26 px, 4:818 finit a
         876 sur une barre a 902). Sans lui, les 40 historiques. */
      /* `retraitHaut` : un nombre (px) ou une longueur CSS ecrite (`clamp(...)`),
         pour un format dont la zone haute doit ceder sur une fenetre plus basse
         que sa planche (quiz, 3:302251, 03/10/2026). */
      padding: sansMargesLaterales
        ? [24, 32, 40, 48].map((def, i) => `${pastilleAvecImage ? RETRAIT_ILLUSTRE[i] : enLongueur(retraitHaut?.[i], def)} 0px 0px`)
        /* ⚠️ RELU DANS FIGMA (22/09) — le conteneur, pas le bloc de texte.
           LATERAL : 40 px, pas 170. Le node 3:60539 porte `px-[40px]` au palier
           lg et 3:60566 la meme chose au palier sm (21 et 22 occurrences). Les
           1000 px que le code cherchait a reproduire sont un `max-w-[1000px]`
           pose sur le BLOC DE TEXTE 3:60544, pas une marge de conteneur — il
           reste porte par LARGEUR_QUESTION, a sa place.
           La recomposition du cadre video le confirme : 40 + (1432 - 1100)/2 =
           206, exactement la position du node 3:20294.
           HAUT : 120 px en lg, 160 px en sm SUR LES PLANCHES DE TEXTE.
           ⚠️ CE RETRAIT N'EST PAS UNIFORME. La planche VIDEO pose 80 px (node
           3:20294 a y=80 dans le cadre de contenu), pas 120. Je l'avais
           generalise depuis la planche 02 : une instance prise pour une regle,
           d'ou 91 px de trop, mesures sur LAB. D'ou le prop `retraitHaut`, que
           chaque format passe s'il differe.
           BAS : 40 px aux deux paliers mesures ; le code n'en posait AUCUN, et
           le contenu touchait la bande basse.
           ⚠️ xs et md n'ont pas de planche : md suit les deux paliers mesures,
           qui valent 40 tous les deux ; xs garde sa gouttiere de lot 1 et
           recoit le meme bas de 40. DEDUIT pour ces deux paliers. */
        /* ⛔ PALIER xs : PAS de marge basse, faute de planche. J'y avais DEDUIT
           les 40 px des deux paliers mesures, puis je les ai retires en croyant
           qu'ils causaient un debordement de 37 px sur un QCM Mobile. VERIFIE :
           ils n'y etaient pour rien, le debordement venait d'un contenu long
           dans une bande qui DEFILE, donc atteignable. Le retrait reste juste,
           mais pour la seule bonne raison : aucune planche ne donne cette cote
           au palier Mobile, donc on n'invente rien. */
        /* xs : 24 par defaut, ou la valeur que le format passe. La planche 02
           Mobile pose 80 (3:60611, padding haut 80) ; d'autres planches Mobile
           different (quiz 08 : la pastille a 171 au lieu de 199) : chaque format
           passe donc la sienne, au fur et a mesure que sa section est controlee. */
        : [`${pastilleAvecImage ? RETRAIT_ILLUSTRE[0] : enLongueur(retraitHaut?.[0], 24)} ${GOUTTIERE_MOBILE}px 0px`,
           /* Tablet illustre : 0 sous le contenu (la bande se colle en bas),
              sauf si le format demande sa marge (`basEmpile` : texte, 40). */
           `${pastilleAvecImage ? RETRAIT_ILLUSTRE[1] : enLongueur(retraitHaut?.[1], 160)} 40px ${pastilleAvecImage ? `${basEmpile}px` : enLongueur(retraitBas?.[1], 40)}`,
           /* ⛔ 01/10/2026 — AUCUN RETRAIT VERTICAL SUR UNE PLANCHE ILLUSTREE.
              Les 120 px du haut et les 40 du bas sont releves SUR LES PLANCHES DE
              TEXTE — le commentaire ci-dessus le dit deja. La planche illustree
              fait partir ses deux colonnes juste sous l'en-tete et les mene au bas
              de l'ecran : node 3:100870, y=75 h=907 sur une planche de 982
              (75 + 907 = 982 a l'unite), et 3:100868, y=77 h=905.
              Garder le retrait coutait 160 px sur 907 : la zone au-dessus de la
              bande tombait a 154 px la ou la planche en donne 507 (node 3:100871),
              et l'enonce venait coller a la bande — le « texte trop pres » du
              01/10. Le lateral de 40 reste : la rangee en ressort deja par ses
              marges negatives, et les ecrans sans image en ont besoin. */
           `${pastilleAvecImage ? "0px" : enLongueur(retraitHaut?.[2], 120)} 40px ${pastilleAvecImage ? "0px" : enLongueur(retraitBas?.[2], 40)}`,
           `${pastilleAvecImage ? "0px" : enLongueur(retraitHaut?.[3], 120)} 40px ${pastilleAvecImage ? "0px" : enLongueur(retraitBas?.[3], 40)}`],
      overflowX:"hidden",
      overflowY:onlyContent?"hidden":"auto",
      display:"flex",
      flexDirection:"column",
      /* ⛔ SEULEMENT SUR LES ECRANS CONCERNES. Quand une image occupe la
         moitie droite, une pastille centree sur la page passe dessous et se
         fait couper : tout doit se ferrer a gauche. Mais brancher la regle sur
         le seul mode d'apercu la faisait deborder sur des ecrans SANS image —
         constate au banc, le titre « Six situations, six reflexes » basculait a
         gauche sans raison. Le decor d'un ecran ne doit dependre que de ce que
         cet ecran contient. */
      alignItems: cote === "gauche" ? "flex-end" : cote === "droite" ? "flex-start" : "center",
      /* ⛔ L'ANCRE DE L'IMAGE PLEINE HAUTEUR. Sans elle, le bloc absolu se
         calait sur la RACINE du format, c'est-a-dire sur les 100 vh : il
         passait donc SOUS le bandeau du haut et SOUS celui du bas. Constat sur
         un ecran 1903 x 991 : la pastille du module devenait illisible sur la
         photo et le bouton « Valider » disparaissait a moitie.
         Ancre ici, l'image ne peut plus deborder de la zone de contenu, par
         construction et non par un calcul de hauteur a maintenir. */
      position: cote ? "relative" : "static",
      justifyContent:"flex-start",
      flex:1,
      minHeight:0,
    }}>
      {/* PART NAME */}
      {/* ⛔ POINT 6, SECONDE MOITIE. La pastille est masquee sur les formats de
          QUESTION (decision L9 d'une revue precedente, a ne pas defaire). Mais
          la masquer en ne la rendant PAS faisait remonter le titre de 68 px sur
          ces ecrans : mesure au navigateur, titre a 205,9 px sur un ecran de
          contenu et 138 px sur un quizz. L'ancrage haut ne suffisait donc pas,
          le titre sautait toujours d'un format a l'autre.
          On rend desormais la pastille TOUJOURS, et on la rend invisible quand
          elle ne doit pas se voir : sa place reste prise, le titre ne bouge
          plus. `visibility` et non `display` — c'est toute la difference. Un
          espace insecable garantit la hauteur meme sans titre de partie. */}
      {
        <Box
          aria-hidden={actPart ? undefined : true}
          sx={{
            /* ⚠️ LA RESERVE N'A DE SENS QUE SI LE FORMAT PEUT EN AVOIR UNE.
               Reserver la place empeche le titre de sauter d'un ecran a l'autre :
               c'est juste. Mais un format qui declare `noPartInfos` n'aura JAMAIS
               de pastille, et sa planche n'en montre pas — la planche video pose
               son cadre a y=80, sans rien au-dessus.
               Mesure sur LAB : le cadre video tombait a y=206 au lieu de 155,
               soit 51 px = 31 de pastille + 20 de marge. Exactement la reserve. */
            visibility: actPart ? "visible" : "hidden",
            flexShrink:0,
            position:"relative",
            zIndex:1,
            display:"flex",
            /* ⚠️ PASTILLE RELUE DANS FIGMA (22/09). Le cadre fait 172,235 x 31 en
               Desktop et Tablet (69 occurrences), et TOUTES ses cotes se
               recomposent a l'unite :
                 hauteur  8 + 15 (icone) + 8 = 31
                 largeur  8 + 13,235 (icone) + 6 (gap) + 137 (texte) + 8 = 172,235
               Deux equations independantes qui tombent juste : ce ne sont pas
               des approximations. */
            gap:"6px",
            alignItems:"center",
            /* Tag « sous-chapitre » : bordure ACCENT bleu #478DFF (jeton du
               design Valentin 2026, OBSERVED sur les formats de contenu), au
               lieu du noir. Jeton central palette.accent (theme). */
            /* ⚠️ LE FOND MANQUAIT. Figma empile deux remplissages sur ce cadre :
               un blanc OPAQUE, puis rgba(118,214,255,0.06) par-dessus, soit
               #F7FDFF composite. Le cadre du code etait transparent et laissait
               passer le fond de la page. Le remplissage du bas etant opaque, la
               couleur ne depend pas de ce fond. */
            backgroundColor:"#F7FDFF",
            border:"1px solid",
            borderColor:"accent.main",
            /* 7 px et non 8 : la bordure Figma est INTERIEURE et mange 1 px du
               padding, alors que `border` CSS s'ajoute a la boite.
               7 + 1 + 15 + 1 + 7 = 31 a l'unite ; poser 8 donnerait 33. */
            padding:"7px",
            overflow:"hidden",
            /* 20 px : `gap-[20px]` sur le conteneur, identique en lg (3:60539) et
               en sm (3:60566). */
            marginBottom:"20px",
            userSelect:"none",
            /* ⛔ CAUSE B, ordre vertical (21/09/2026). Les planches Tablet et
               Mobile des variantes ILLUSTREES placent la pastille SOUS le media ;
               rendue ici, elle est au-dessus. On ne deplace pas ce noeud — la
               place qu'il reserve empeche le titre de sauter d'un format a
               l'autre — on le masque aux paliers empiles, ou la rangee illustree
               en rend un second au bon endroit.
               ⚠️ DOIT RESTER APRES le `display:"flex"` ci-dessus, sinon elle est
               ecrasee en silence. */
            /* Point 4 (30/09/2026) : AVEC une image, celle-ci s'efface et la
               colonne porte la sienne — sinon elle reste centree sur la planche,
               loin de son bloc. SANS image, rien ne change. */
            ...(pastilleDansColonne
                 ? { display: (pastilleAvecImage || pastilleEnTete) ? "none" : ["none", "none", "flex", "flex"] }
                 : {}),
            /* ⚠️ ET CELLE-CI EN DERNIER AUSSI, pour la meme raison. Posee plus
               haut, elle etait ecrasee en silence par le `display:"flex"` — le
               piege que le commentaire ci-dessus decrit, et dans lequel je suis
               tombe. Mesure DOM sur LAB : la pastille restait en `display:flex`,
               hauteur 31 plus 20 de marge, et poussait le cadre video de 155 a
               206. Un format qui declare `noPartInfos` n'aura jamais de
               pastille : sa place n'a pas a etre reservee. */
            ...(noPartInfos === true ? { display:"none" } : {}),
            [FENETRE_TRES_BASSE]: { display:"none" },
          }}
        >
          {/* ⚠️ L'ICONE NE GROSSIT PAS AVEC LE PALIER : 13,235 x 15 a l'identique
              en Desktop et en Mobile. Le code montait a 16 px de large, donc
              17,88 de haut avec le ratio 17:19 du fichier SVG — a lui seul cet
              excedent faisait deja deborder le cadre de 31 px.
              La hauteur est posee EXPLICITEMENT, sinon le ratio du fichier la
              recalcule et rate le 15. */}
          <Box sx={{width:"13.235px", minWidth:"13.235px", height:"15px"}}>
            {/* icone teintee en bleu accent pour ce tag (filtre approx #478DFF),
                localement — les icones du header/footer restent sombres. */}
            <img src={moduleMiniIcon} style={{width:"100%", height:"100%", display:"block", filter:"brightness(0) saturate(100%) invert(45%) sepia(83%) saturate(2400%) hue-rotate(203deg) brightness(101%) contrast(101%)"}} />
          </Box>
          <Box sx={{
            /* ⚠️ 14 px, jeton « Text/Desktop/Label/Small » (node 3:60543), 13 en
               Mobile (3:60615). Le code posait 20 : +43 %. Le meme jeton avait
               deja ete corrige le 21/09 sur la pastille de CHAPITRE du bandeau ;
               celle de SOUS-chapitre etait restee.
               ⚠️ L'interligne est pose a 15 px, et c'est indispensable : sans
               lui la boite de texte retombe sur `normal` (~1,17), redevient plus
               haute que l'icone et pilote la hauteur du cadre a sa place. Poser
               les 26 px de Figma serait faux aussi — la maquette les annule par
               `text-box-trim`, que le navigateur n'applique pas ici. */
            fontSize:["13px", "14px"],
            lineHeight:"15px",
            color:"accent.main",
            whiteSpace:"noWrap",
            overflow:"hidden",
            textOverflow:"ellipsis",
          }}>
            {actPart?.title || "\u00A0"}
          </Box>
        </Box>
      }
      {/* END PART NAME */}
      {/* Les variantes illustrees passent une FONCTION : elles recoivent la
          pastille et la posent elles-memes dans la colonne de contenu, ou elle
          n'apparait qu'aux paliers empiles. Les autres appelants passent du
          JSX et ne changent pas de chemin. */}
      {typeof children === "function"
        ? children({ pastille: (
            /* `aria-hidden` : la pastille de page, en double, porte le nom pour
               les lecteurs d'ecran — sauf en tete de colonne, ou c'est la seule
               (relecture du 04/10) */
            <Box aria-hidden={pastilleEnTete ? undefined : true} sx={{
              /* Point 4 (30/09/2026) : avec une image, elle vaut a tous les
                 paliers et celle de la page s'efface. Sans image, rien ne change :
                 elle reste reservee aux paliers empiles. */
              display: (pastilleAvecImage || pastilleEnTete) ? "flex" : ["flex", "flex", "none", "none"],
              alignSelf: pastilleEnTete ? "flex-start" : "center", flexShrink:0,
              /* ⚠️ PASTILLE RELUE DANS FIGMA (22/09). Le cadre fait 172,235 x 31 en
               Desktop et Tablet (69 occurrences), et TOUTES ses cotes se
               recomposent a l'unite :
                 hauteur  8 + 15 (icone) + 8 = 31
                 largeur  8 + 13,235 (icone) + 6 (gap) + 137 (texte) + 8 = 172,235
               Deux equations independantes qui tombent juste : ce ne sont pas
               des approximations. */
            gap:"6px", alignItems:"center",
              border:"1px solid", borderColor:"accent.main",
              /* Le meme fond que la pastille de page (#F7FDFF, deux remplissages
                 empiles sur 3:60540). Cette variante, celle des paliers empiles,
                 l'avait perdu : pastille transparente en Tablet et en Mobile
                 (3:60567, 3:60612), constate par le comparateur. */
              backgroundColor:"#F7FDFF",
              /* 7 px et non 8 : la bordure Figma est INTERIEURE et mange 1 px du
               padding, alors que `border` CSS s'ajoute a la boite.
               7 + 1 + 15 + 1 + 7 = 31 a l'unite ; poser 8 donnerait 33. */
            padding:"7px",
              /* 20 px ENTRE PASTILLE ET CONTENU, comme sur les 66 planches
                 (3:60539, 3:60566). ⛔ 01/10/2026 (nuit) : cette pastille est
                 rendue DANS `BlocIllustre`, dont la colonne pose deja un `gap`
                 de 16 px (xs) et 20 px (sm) ; ses 20 px s'y ajoutaient — 40 en
                 Tablet, 36 en Mobile. Elle ne porte donc que le complement : 4
                 en xs, 0 en sm, et les 20 entiers en md et lg, ou la colonne
                 des moities n'a pas de `gap`. */
              overflow:"hidden", marginBottom: Array.isArray(pastilleEnTete) ? pastilleEnTete : ["4px", "0px", "20px", "20px"], userSelect:"none",
              /* Fenetre courte avec image (B85, 04/10) : la pastille est dans
                 la moitie de contenu, qui n'a pas de `gap` — les 20 entiers. */
              ...(pastilleAvecImage ? { [FENETRE_COURTE]: { marginBottom:"20px" } } : {}),
              [FENETRE_TRES_BASSE]: { display:"none" },
              visibility: actPart ? "visible" : "hidden",
            }}>
              {/* ⚠️ L'ICONE NE GROSSIT PAS AVEC LE PALIER : 13,235 x 15 a l'identique
              en Desktop et en Mobile. Le code montait a 16 px de large, donc
              17,88 de haut avec le ratio 17:19 du fichier SVG — a lui seul cet
              excedent faisait deja deborder le cadre de 31 px.
              La hauteur est posee EXPLICITEMENT, sinon le ratio du fichier la
              recalcule et rate le 15. */}
          <Box sx={{width:"13.235px", minWidth:"13.235px", height:"15px"}}>
                <img src={moduleMiniIcon} style={{width:"100%", height:"100%", display:"block", filter:"brightness(0) saturate(100%) invert(45%) sepia(83%) saturate(2400%) hue-rotate(203deg) brightness(101%) contrast(101%)"}} />
              </Box>
              <Box sx={{
                /* ⛔ 01/10/2026 — 14 px, ET DEUX MESURES INDEPENDANTES LE DISENT.
                   Le code posait 20 px sans aucune source. Node 3:60547, le
                   texte « Nom du sous chapitre » de la planche 02 : 137 de large
                   pour 9 de haut.
                     — la hauteur : le fichier porte `text-box-trim`, et son
                       rapport hauteur/corps est constant (nom du module 18 px
                       pour 12 de haut, nom du chapitre 14 px pour 9). 9 / 0,667
                       = 13,5, donc 14.
                     — la largeur : « Nom du sous chapitre » fait 20 caracteres.
                       137 / 20 = 6,9 px par caractere, ce qui correspond a 14 px
                       de corps en Questrial. A 20 px, la chaine ferait pres de
                       200 et ne tiendrait pas dans la pastille de 172,235.
                   C'est le MEME jeton que le nom de chapitre de l'en-tete
                   (3:20281), deja releve a 14 le 21/09.
                   ⚠️ L'INTERLIGNE FAIT LA HAUTEUR DE LA PASTILLE. Sans valeur
                   posee, le navigateur retombe sur `normal` : la ligne mesurait
                   23 px, et la pastille 7+1+23+1+7 = 39 au lieu des 31 du node.
                   Les 8 px en trop poussaient vers le bas TOUT le contenu de la
                   colonne illustree — titre, carte, bande. */
                /* 13 px en Mobile, comme la pastille de page (3:60615). */
                fontSize:["13px", "14px"], lineHeight:"15px",
                color:"accent.main", whiteSpace:"noWrap", overflow:"hidden", textOverflow:"ellipsis"}}>
                {actPart?.title || "\u00A0"}
              </Box>
            </Box>
          ) })
        : children}
    </Box>
  )
}

/* ⛔ POINTS 9 ET 13 DE LA REVUE APPRENANT (Nawal, 04/09) — LA REGLE DES BLOCS.
 *
 * Elle structure tout le systeme, et le Figma l'applique a TOUTES les variantes
 * illustrees, quel que soit le format : image CARREE a gauche sur environ 28 %
 * de la largeur, contenu a droite sur environ 38 %, les deux blocs alignes en
 * haut ET en bas, de meme hauteur.
 *
 * Ce qui existait : image rectangulaire a 40 % (`maxHeight: 60vh`,
 * `objectFit: contain`), contenu a 50 %, et l'image descendait deux fois plus
 * bas que le texte — le bloc de texte flottait en haut a droite avec un grand
 * vide dessous.
 *
 * Sans image, la regle du point 13 s'applique : une ligne de texte ne depasse
 * pas ~720 px. Mesure avant correctif : 1100 px sur un ecran de 1440. Au dela,
 * l'oeil perd le debut de la ligne suivante — c'est une regle de lisibilite,
 * pas une preference.
 *
 * Les largeurs sont en `vw` parce que la fiche les exprime en pourcentage de
 * l'ECRAN, pas du conteneur. Les bornes en px evitent qu'un tres grand ecran
 * n'etire les blocs au dela du raisonnable. */
/* ⛔ POINT 16 DE LA REVUE APPRENANT (Nawal, 04/09) — LE DEBORDEMENT.
   Le Figma ne disait rien de ce qui doit arriver quand le contenu depasse la
   hauteur du cadre : sur la maquette elle-meme, le texte se fait couper par le
   bandeau, la grille du Diagnostic perd une rangee et la liste de fin de module
   perd une ligne. Decision prise dans la fiche, a appliquer PARTOUT : le bloc
   garde sa hauteur, le contenu defile a l'interieur, et la barre de defilement
   est VISIBLE sur son bord droit — sans quoi l'apprenant ne sait pas qu'il
   reste du contenu. Les autres elements de l'ecran ne bougent pas.
   Une barre laissee au navigateur ne se montre qu'une fois qu'on fait defiler :
   elle ne previent donc personne. On la dessine. */
/* ⛔ L'ENONCE EST ROGNE EN HAUT, COMME LA MAQUETTE (02/10/2026, PD-16).
   Les textes Figma sont rognes a la hauteur des capitales (`leadingTrim:
   CAP_HEIGHT`) : 20 px sous la pastille, ce sont 20 px jusqu'au HAUT DES
   LETTRES. Le navigateur gardait la demi-interligne au-dessus de la premiere
   ligne : l'enonce tombait 8 px trop bas en Desktop et Tablet, 7 en Mobile,
   mesure par le comparateur sur les planches 02 et 03 (enonces 3:60545,
   3:60617, 3:80674). Verifie au pixel apres : haut des lettres a y=246, comme
   le noeud 3:60545.
   `trim-start` seulement : le bas n'est pas rogne, donc tout ce qui suit
   l'enonce remonte du meme nombre de pixels et ses espacements ne changent pas,
   y compris sur les formats que le comparateur ne controle pas encore.
   Chrome, Edge et Safari l'appliquent ; Firefox l'ignore et garde l'ancien rendu,
   ce qui est accepte : Rising Up recommande Chrome ou Safari (Hicham, 02/10). */
export const ROGNE_HAUT = { textBox: "trim-start cap alphabetic" }

/* ⛔ ET LA COLONNE QUI LE PORTE GARDE DE LA PLACE AU-DESSUS (02/10/2026).
   Rogne, l'enonce commence au haut des CAPITALES : l'accent d'un « É », d'un
   « À », d'un « Ê » se dessine au-dessus de sa boite — 7,1 px a 42 px, 4,4 a
   26 px (mesure dans Questrial). La colonne qui le porte defile, donc COUPE ce
   qui depasse : « Écrivez » s'affichait « Ecrivez ». Trouve par la relecture du
   code ; les enonces du banc n'avaient aucune majuscule accentuee.
   Une reserve en haut, annulee par une marge negative egale : l'enonce reste ou
   la maquette le pose (3:60545), et les accents ont leur place.
   Teste par `filet/conformite/accents.mjs`, vu rouge avant (45 enonces coupes). */
export const RESERVE_ACCENTS = {
  paddingTop:["6px", "10px", "10px", "10px"],
  marginTop:["-6px", "-10px", "-10px", "-10px"],
}

/* (04/10/2026) `HAUTEUR_BLOC_DEFILANT` (`100cqh - 460px`) est retiree : plus
   aucun bloc ne l'employait — la liste de fin de module prend la hauteur qui
   reste (FinDeFormation.js), celle du Diagnostic aussi (PD-13). */

export const BLOC_DEFILANT = {
  overflowY:"auto",
  /* `stable` reserve la gouttiere en permanence : sans elle, la barre est en
     SURIMPRESSION sur macOS et n'apparait que pendant le defilement — elle ne
     previent donc personne, ce qui est precisement ce que la fiche demande.
     Mesure avant : gouttiere de 0 px. */
  scrollbarGutter:"stable",
  overflowX:"hidden",
  scrollbarWidth:"thin",
  scrollbarColor:"rgba(10,10,11,0.28) transparent",
  "&::-webkit-scrollbar" : { width:"6px" },
  "&::-webkit-scrollbar-track" : { background:"transparent" },
  "&::-webkit-scrollbar-thumb" : { background:"rgba(10,10,11,0.28)" },
  paddingRight:"4px",
  /* ⛔ 01/10/2026 — `border-box`, MEME CAUSE QUE LA CARTE DU QUIZ.
     Ces 4 px de gouttiere s'AJOUTAIENT a la largeur de l'enfant : relevé
     automatique des debordements sur les 29 ecrans x 3 largeurs, le bloc
     defilant du Diagnostic et de la fin de module depassait son parent de 4 px
     aux TROIS largeurs — 1324 dans 1320, 758 dans 754, 357 dans 353.
     Quatre pixels ne se voient pas a l'oeil ; ils se voient quand la liste est
     la seule chose alignee sur la colonne, et ils se verront le jour ou la
     gouttiere grandira. */
  boxSizing:"border-box",
}

/* ⚠️ LA BARRE DE DEFILEMENT NE SUFFIT PAS, ET C'EST MESURE.
   La fiche demande une barre « visible sur le bord droit, pour que l'apprenant
   sache qu'il reste du contenu ». Or Chromium sur macOS impose des barres en
   SURIMPRESSION : elles n'apparaissent que PENDANT le defilement. Mesure sur le
   bloc du bilan : gouttiere de 0 px, avec `::-webkit-scrollbar` style ET
   `scrollbar-gutter: stable`. Une barre qui ne se montre qu'une fois qu'on a
   defile ne previent personne — elle repond a l'inverse du besoin.
   D'ou ce fondu, pose au bas du bloc : il est la AU REPOS, il ne depend
   d'aucun reglage systeme, et c'est le signal que tout le monde lit. La barre
   stylee reste, pour les machines qui en affichent une. */
/* ⚠️ La couleur par defaut est celle du fond sur lequel ce fondu est REELLEMENT
   pose — la bande teintee du quiz et la page teintee du diagnostic, les deux
   seuls appelants. Mesuree au navigateur : `rgb(250, 248, 246)`. J'avais ecrit
   #FAF8F7 de memoire, une unite d'ecart sur le bleu : invisible a l'oeil, mais
   un degrade qui n'arrive pas exactement a la couleur du fond laisse une barre
   sur les ecrans qui ne lissent pas.
   Lot G : c'est la MEME teinte que `FOND_BANDE_BASSE`, et elle n'est plus
   dupliquee — deux constantes egales finissent toujours par diverger. */

/* ⛔ 01/10/2026 — LE FONDU NE S'AFFICHE QUE S'IL RESTE QUELQUE CHOSE DESSOUS.
   Deux griefs d'Hicham, le meme jour, et une seule regle les reglait :
     « quand on doit pas scroller, pas de fondu, sinon c'est bizarre »
     « quand on doit afficher des explications, il faut qu'on le voit mieux »
   Le fondu etait pose EN PERMANENCE au bas de la bande. Deux consequences,
   visibles sur ses captures :
     - sur un ecran qui ne defile pas, il voilait le bas pour ne rien annoncer ;
     - le bloc « Bonne reponse » / « Mauvaise reponse » est la DERNIERE chose du
       bloc defilant : une fois l'apprenant en bas, le fondu tombait pile dessus.
       « Bravo » et « Oups, ne t'inquiete pas... » etaient quasi illisibles.
   La regle tient en une phrase : le fondu previent qu'il reste du contenu, donc
   il disparait des qu'il n'en reste plus — y compris quand on arrive en bas, ce
   qui decouvre l'explication au moment exact ou on la lit.
   On mesure, on ne devine pas : `scrollHeight - scrollTop - clientHeight`. Trois
   sources de changement, et il faut les trois — le defilement, la taille de la
   boite (fenetre), et le CONTENU (la correction qui s'affiche apres validation
   change la hauteur sans toucher a la boite). */
export const useDebordeEnBas = () => {
  const ref = useRef(null)
  const [deborde, setDeborde] = useState(false)
  useEffect(() => {
    const n = ref.current
    if (!n) return undefined
    const mesurer = () => {
      setDeborde(n.scrollHeight - n.scrollTop - n.clientHeight > 1)
    }
    mesurer()
    n.addEventListener("scroll", mesurer, { passive: true })
    /* ⚠️ `ResizeObserver` et `MutationObserver` manquent a certains bancs : on
       les declare optionnels plutot que de faire tomber l'ecran. Le fondu reste
       alors fige sur sa premiere mesure, ce qui est le pire acceptable. */
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(mesurer) : null
    if (ro) ro.observe(n)
    const mo = typeof MutationObserver !== "undefined" ? new MutationObserver(mesurer) : null
    if (mo) mo.observe(n, { childList: true, subtree: true, characterData: true })
    return () => {
      n.removeEventListener("scroll", mesurer)
      if (ro) ro.disconnect()
      if (mo) mo.disconnect()
    }
  }, [])
  return [ref, deborde]
}

/* ⛔ 06/10/2026 — « IL RESTE DU TEXTE DESSOUS », QUEL QUE SOIT CE QUI DEFILE.
   Consigne de Nawal (format Texte, texte long) : un fondu au bas de la BOITE de
   texte tant qu'il reste du contenu a lire, plus rien quand on est en bas ou
   quand tout tient. `useDebordeEnBas` ne regarde que le defilement de
   l'element lui-meme ; or la carte de texte ne defile elle-meme qu'en deux
   colonnes. Empilee (tablette, telephone) ou sans image, c'est la PAGE qui
   defile et la carte la depasse. Ce crochet couvre les deux :
     - la carte defile : il reste du contenu sous son bas visible ;
     - un ancetre defile (ou la fenetre) : le bas de la carte est sous le bas
       visible de cet ancetre.
   Re-mesure au defilement de N'IMPORTE QUEL element (ecoute en capture : un
   defilement ne remonte pas), au redimensionnement, et quand le contenu change. */
export const useSuiteCachee = () => {
  const ref = useRef(null)
  const [etat, setEtat] = useState({ cachee: false })
  useEffect(() => {
    const n = ref.current
    if (!n) return undefined
    const mesurer = () => {
      /* La marge interieure du bas n'est que du blanc : quand il n'en reste
         plus qu'elle a decouvrir, tout le texte est lisible. */
      const cs = window.getComputedStyle(n)
      const blancBas = parseFloat(cs.paddingBottom) || 0
      let suivant = { cachee: false }
      if (n.scrollHeight - n.scrollTop - n.clientHeight - blancBas > 1) {
        /* la carte defile elle-meme */
        suivant = { cachee: true, mode: "carte", blancBas: Math.round(blancBas) }
      } else {
        let a = n.parentElement
        while (a && a !== document.body && a !== document.documentElement) {
          const oy = window.getComputedStyle(a).overflowY
          if ((oy === "auto" || oy === "scroll") && a.scrollHeight - a.clientHeight > 1) break
          a = a.parentElement
        }
        const ancetre = a && a !== document.body && a !== document.documentElement ? a : null
        const basVisible = ancetre
          ? ancetre.getBoundingClientRect().top + ancetre.clientTop + ancetre.clientHeight
          : window.innerHeight
        const r = n.getBoundingClientRect()
        const bord = parseFloat(cs.borderBottomWidth) || 0
        if (r.height > 0 && (r.bottom - bord - blancBas) - basVisible > 1) {
          /* c'est la page qui defile : le fondu se pose au bas VISIBLE de la
             carte, mesure depuis son bord haut interieur */
          suivant = { cachee: true, mode: "page", haut: Math.round(basVisible - r.top - (parseFloat(cs.borderTopWidth) || 0)) }
        }
      }
      setEtat((e) => (e.cachee === suivant.cachee && e.mode === suivant.mode && e.haut === suivant.haut && e.blancBas === suivant.blancBas) ? e : suivant)
    }
    mesurer()
    document.addEventListener("scroll", mesurer, { capture: true, passive: true })
    window.addEventListener("resize", mesurer)
    /* Une image qui finit de charger AU-DESSUS de la carte la deplace sans
       changer sa taille : on observe donc aussi ses ancetres (leur taille
       change, eux), et la fin des chargements d'images et de polices. */
    document.addEventListener("load", mesurer, true)
    const polices = typeof document !== "undefined" && document.fonts && document.fonts.addEventListener ? document.fonts : null
    if (polices) polices.addEventListener("loadingdone", mesurer)
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(mesurer) : null
    if (ro) {
      for (let a = n; a && a !== document.body && a !== document.documentElement; a = a.parentElement) ro.observe(a)
    }
    const mo = typeof MutationObserver !== "undefined" ? new MutationObserver(mesurer) : null
    if (mo) mo.observe(n, { childList: true, subtree: true, characterData: true })
    return () => {
      document.removeEventListener("scroll", mesurer, { capture: true })
      window.removeEventListener("resize", mesurer)
      document.removeEventListener("load", mesurer, true)
      if (polices) polices.removeEventListener("loadingdone", mesurer)
      if (ro) ro.disconnect()
      if (mo) mo.disconnect()
    }
  }, [])
  return [ref, etat]
}

/* « #RRGGBB » -> « rgba(r,g,b,a) ». Un degrade doit partir de la MEME couleur
   en transparent : sinon Safari interpole en passant par le noir transparent,
   et le bas du texte grisaille. */
const enTransparence = (hex, alpha) => {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(String(hex || ""))
  if (!m) return null
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${alpha})`
}

/* `suite` (06/10/2026, format Texte) : le fondu vit DANS le bloc de texte, et
   se pose au bas VISIBLE de ce bloc, jamais en travers de la page. `suite`
   est l'etat rendu par `useSuiteCachee` :
     - la carte defile elle-meme : fondu colle (`sticky`) au bas de sa zone de
       defilement, marge interieure comprise (sans quoi le texte reapparaitrait
       net sous le fondu) ;
     - la page defile : fondu pose a la hauteur du bas visible, mesuree (la
       carte est elle-meme un conteneur `overflow: auto`, un `sticky` s'y
       accrocherait et resterait en bas de la carte, hors de vue).
   Rien n'est monte s'il ne reste rien a lire.
   ⛔ 06/10/2026 (Nawal : « je ne vois pas le flou ») : la premiere version,
   44 px de simple degrade, ne palissait que la derniere ligne. L'effet couvre
   desormais les 2 a 3 dernieres lignes visibles (90 px a l'ordinateur, 80 en
   tablette, 64 au telephone) et superpose trois couches :
     - un FLOU LEGER (1,5 px) qui monte depuis le haut de l'effet ;
     - un FLOU FORT (4 px) sur les 60 % du bas ;
     - le DEGRADE vers la couleur de la boite, presque opaque en bas.
   Chaque flou est borne par un masque en degrade : nul en haut, plein en bas.
   Le texte reste net jusqu'au debut de l'effet ; la derniere ligne est presque
   effacee. `-webkit-` pour Safari. Sans flou (navigateur qui ne le gere pas ;
   le WebKit de test de Playwright, en mode sans ecran, n'en dessine aucun), le
   degrade seul reste, et il suffit a lui seul a se voir. */
const HAUTEUR_FONDU_SUITE = ["64px", "80px", "90px", "90px"]
const flou = (px, debut) => ({
  position:"absolute", top:0, left:0, right:0, bottom:0,
  WebkitBackdropFilter:`blur(${px}px)`,
  backdropFilter:`blur(${px}px)`,
  WebkitMaskImage:`linear-gradient(to bottom, transparent ${debut}%, black 100%)`,
  maskImage:`linear-gradient(to bottom, transparent ${debut}%, black 100%)`,
})
export const FonduBas = ({ couleur, suite }) => {
  const fin = couleur || FOND_BANDE_BASSE
  if (suite) {
    if (!suite.cachee) return null
    const t = (a) => enTransparence(fin, a) || `rgba(255,255,255,${a})`
    const enPage = suite.mode === "page"
    return (
      <Box aria-hidden data-fondu-bas={suite.mode} sx={{
        flexShrink:0,
        height:HAUTEUR_FONDU_SUITE,
        pointerEvents:"none",
        position: enPage ? "absolute" : "sticky",
        ...(enPage
          ? { left:0, right:0, top:suite.haut + "px", transform:"translateY(-100%)" }
          : { bottom:(-(suite.blancBas || 0)) + "px", marginTop:HAUTEUR_FONDU_SUITE.map((h) => "-" + h) }),
        /* Telephone couche (450 px de haut au plus) : deux lignes visibles a
           peine ; un fondu plus court pour ne pas en manger une. */
        [FENETRE_TRES_BASSE]: enPage ? { height:"30px" } : { height:"30px", marginTop:"-30px" },
      }}>
        <Box data-fondu-couche="flou-leger" sx={flou(1.5, 0)} />
        <Box data-fondu-couche="flou-fort" sx={flou(4, 40)} />
        <Box data-fondu-couche="degrade" sx={{
          position:"absolute", top:0, left:0, right:0, bottom:0,
          background:`linear-gradient(to bottom, ${t(0)} 0%, ${t(0.3)} 25%, ${t(0.65)} 55%, ${t(0.9)} 80%, ${fin} 100%)`,
        }} />
      </Box>
    )
  }
  return (
    <Box aria-hidden sx={{
      position:"absolute",
      left:0, right:0, bottom:0,
      height:["28px", "34px", "38px", "40px"],
      pointerEvents:"none",
      /* Le depart doit etre la MEME couleur en transparent, sinon Safari
         interpole vers du gris en passant par le noir transparent. */
      background:"linear-gradient(to bottom, rgba(250,248,246,0), " + fin + ")",
    }}/>
  )
}

/* ⛔ Trouve par la batterie de tests du 07/09 sur le vrai catalogue : un quiz
   de STAY FOCUS ON B2 commence par SEPT sauts de ligne tapes dans le contenu.
   Les champs sont rendus en `white-space: pre-line`, qui les respecte : la
   question descendait, et les reponses Vrai/Faux passaient sous le bord. Trente
   contenus ont ce defaut (6 questions, 1 titre, 23 textes). On retire les
   blancs de tete et de queue au rendu ; le contenu, lui, n'est pas touche. */
export const sansBlancsAutour = (v) => (typeof v === "string" ? v.replace(/^(\s|<br\s*\/?>)+|(\s|<br\s*\/?>)+$/gi, "") : v)

/* ⚠️ N'EST PLUS LA LARGEUR DE LECTURE D'UN TEXTE (21/09/2026, lot 2).
   Cette valeur de 720 px n'a AUCUNE contrepartie dans les planches : Figma donne
   une colonne de 1000 et une carte de 600, pas 720 pour les deux. Elle ne sert
   donc plus qu'a `FormatFeedback`, QUI N'A AUCUNE PLANCHE (voir plus haut).
   On la lui laisse telle quelle : corriger un ecran sans reference serait
   remplacer une valeur non prouvee par une autre. */
export const LARGEUR_FEEDBACK = "720px"
export const LARGEUR_LECTURE = LARGEUR_FEEDBACK

/* ⚠️ LA CARTE N'A PAS LA LARGEUR DE SA COLONNE (21/09/2026, lot 2).
   C'est un ecart de COMPOSITION, pas de valeur, et c'est ce qu'Hicham voyait
   comme « un texte qui flotte dans une boite trop grande ».
     texte seul : colonne 1000 (node 3:141172), carte 600 (node 3:141174,
                  x=200 dans la colonne, donc centree)
     illustre   : colonne 741 (node 3:181380), carte 700 (node 3:181382,
                  x=20 dans 741, donc centree aussi)
   Le code plafonnait titre ET carte a la meme valeur. Les deux sont desormais
   distincts, et la carte se centre dans sa colonne. */
/* ⛔ 08/10/2026 — ECART VOULU PAR NAWAL a la planche 06 (3:141174, 600) : la
   carte de texte seule passe a 700, la largeur de la carte illustree. Les deux
   variantes du format Texte ont desormais la meme carte (700 px au plus) et le
   meme corps de texte (20 px, voir FormatTexte.js). A noter en PD (Hicham). */
export const LARGEUR_CARTE_TEXTE     = "700px"
export const LARGEUR_CARTE_ILLUSTREE = "700px"
/* ⚠️ LOT H — LES DEUX CHAMPS ONT LA MEME LARGEUR, ET C'EST LA HAUTEUR QUI LES
   DISTINGUE. Mesure sur les deux nodes, meme element dans les deux cas :
     saisie une ligne (3:40519)  -> champ 3:60548, x=381, 750 x 70
     zone de texte    (3:60648)  -> champ 3:80677, x=381, 750 x 200
   Un arbitrage anterieur (« Point 8 ») leur avait donne deux LARGEURS
   differentes, 420 et 540. Il corrigeait un vrai probleme — le champ d'une
   ligne paraissait demesure — mais sur la mauvaise dimension : la planche les
   aligne sur la meme colonne de 750 px, celle des cartes et des controles, et
   c'est la hauteur qui dit « une ligne » ou « un paragraphe ».
   Les deux entrees restent distinctes : la hauteur les separera. */
/* ⚠️ 750 px FIXES, PAS 50 % (22/09/2026, signale par Hicham : « le texte du
   champ n'est meme pas centre, on dirait qu'il est un peu a gauche »).
   Le champ fait 750 a Desktop ET a Tablet : node 3:80677 sur une plaque de 1512
   (381 + 750 + 381) et node de la planche 02 Tablet sur une plaque de 834
   (42 + 750 + 42). Les deux recompositions tombent a l'unite.
   Or 50 % de 834 vaut 417 : ce n'est donc PAS une proportion. Le `50cqw` venait
   d'avoir lu 750/1512 = 49,6 % sur UNE planche et d'en avoir fait une regle —
   les deux lectures collent sur Desktop, seule la planche Tablet departage.
   Consequence : sur toute fenetre plus etroite que ~1500 px, le champ
   retrecissait, et comme il reste centre il donne l'impression d'un decalage.
   ⚠️ `maxWidth` et non `width` : sur une fenetre trop etroite pour 750, le
   champ suit son conteneur au lieu de deborder. Mobile prend la largeur utile. */
export const LARGEUR_CHAMP = {
  uneLigne : ["100%", "750px", "750px", "750px"],
  textarea : ["100%", "750px", "750px", "750px"],
}
/* ⚠️ CORRECTION 21/09/2026 — valeurs relues dans Figma, node `Rectangle 143151`,
   identique sur les planches 04, 05, 06', 09 et 10 :
     Desktop 480 x 600  (480 / 1512 = 31,75 %)
     Tablet  320 x 400  (320 /  834 = 38,4 %)
     Mobile  393 x 314  = pleine largeur
   Le ratio 4/5 en Desktop et Tablet, 5/4 en Mobile, est donc CONFIRME par la
   source. La LARGEUR ne l'etait pas : le Desktop etait plafonne a 420 px alors
   que la planche donne 480, soit 60 px de moins (12,5 %). Meme cause que le
   cadre video de FormatVideo.js : cote relevee sur l'export JPEG.
   Le Tablet, lui, etait juste : le commentaire d'origine disait « image de
   318 px sur une frame de 834, soit 38 % », contre 320 reels. C'est ce
   calibrage-la qui prouve que le `cqw` se rapporte a la largeur de PLAQUE,
   donc que le Desktop doit valoir 31,75cqw et non 28.
   ⚠️ `maxHeight` reste le garde-fou sur fenetre courte : la planche fait
   982 px de haut, un portrait de 600 y tient, mais pas dans 700 px de fenetre. */
export const LARGEUR_IMAGE   = ["100%", "min(38.4cqw, 320px)", "min(31.75cqw, 480px)", "min(31.75cqw, 480px)"]
/* ⚠️ COLONNE ILLUSTREE RELUE DANS FIGMA (21/09/2026, lot 2) : 560 -> 741.
   Node 3:181380, pose a x=40 dans la demi-page droite de 821 (40 + 741 + 40 =
   821 a l'unite). 741 / 1512 = 49,0 %, d'ou le `49cqw`. Le 560 etait un plafond
   estime : il rabotait la colonne de 181 px, soit 24 %, sur les CINQ formats
   illustres (04, 05, 06', 09, 10). La valeur 741 se retrouve sur 9 plaques
   Desktop du fichier.
   Tablet et Mobile restent a 100 % : a ces paliers la composition s'empile et
   la colonne occupe toute la largeur utile, deja corrigee au lot 1. */
export const LARGEUR_CONTENU = ["100%", "100%", "min(49cqw, 741px)", "min(49cqw, 741px)"]

/* Le duo image + contenu. `stretch` aligne les deux blocs en haut et en bas ;
   l'image est carree par `aspectRatio` et remplit son cadre en `cover`.
   ⚠️ Le CONTENU est ecrit avant l'image dans le DOM, et `row-reverse` la ramene
   a gauche a l'ecran (decision L65 d'une revue precedente) : ainsi le rendu
   mobile, qui empile en colonne, montre le texte d'abord et non une image
   qui repousserait la question hors de l'ecran. Ne pas « simplifier » en
   deplacant les blocs. */
/* ⛔ FENETRE LARGE MAIS COURTE. Mesure au banc, 1024 x 640 : la mise en page
   restait EMPILEE alors qu'il y avait de la largeur a revendre. L'image prenait
   166 px des 335 disponibles, et le bloc des propositions tombait a 47 px de
   haut pour 336 px de contenu — l'apprenant voyait une photo, une question, et
   devait deviner qu'il restait quatre reponses.
   Le palier « md » du theme vaut 1130 px : une fenetre de 1024 n'y arrive
   jamais, quelle que soit sa hauteur. La largeur seule ne suffit donc pas a
   decider. Cette regle regarde LES DEUX : des qu'il y a de quoi tenir deux
   colonnes et pas de quoi tenir une pile, on passe en deux colonnes.
   Elle est ecrite ici et nulle part ailleurs : les trois endroits qui en
   dependent l'importent. */
/* ⚠️ La borne HAUTE de largeur est indispensable. Sans elle, un portable de
   1440 x 750 — tres courant une fois la barre du navigateur deduite — tombait
   dans cette regle et voyait son image passer de 403 a 340 px, alors que sa
   mise en page etait deja en deux colonnes et n'avait aucun besoin d'etre
   secourue. La regle ne doit toucher QUE les fenetres qui, sans elle,
   resteraient empilees : en dessous du palier « md » du theme, 1130 px. */
export const FENETRE_COURTE = "@media (min-width:760px) and (max-width:1129px) and (max-height:760px)"

/* ⛔ 04/10/2026 — LA FENETRE TRES BASSE (telephone couche, 360 a 450 px de
   haut). La pastille du sous-chapitre y coute 51 px (31 + 20) sur une hauteur
   utile de 280 environ : mesure a 800 x 360 par la relecture du 04/10, la
   fenetre des reponses du quiz tombait de 93 a 50 px et l'enonce a 10 px une
   fois la pastille remise au-dessus de la question (B85). Le chapitre reste
   dans l'en-tete ; la pastille, qui le precise, s'efface. Aucune planche ne
   dessine cette hauteur. */
export const FENETRE_TRES_BASSE = "@media (max-height:450px)"

/* La colonne de texte qui accompagne l'image. Sa largeur est fixee par
   LARGEUR_CONTENU, qui vaut 100 % tant que le bloc est empile : en fenetre
   courte le bloc passe en deux colonnes, il faut donc lui rendre une largeur
   relative, sans quoi elle deborderait. A spreader dans le `sx` de la colonne. */
export const COLONNE_ILLUSTREE = {
  [FENETRE_COURTE]: {
    width:"min(52cqw, 640px)",
    maxWidth:"100%",
    flex:"unset",
  },
}

/* LA DECOUPE. Reprise telle quelle de l'ancien lecteur (`OLD/public/img/
   mask-fill.svg`), volontairement : pour un arbitrage, Nawal doit reconnaitre
   l'univers qu'elle regrette, pas en decouvrir un troisieme. L'arc pourra etre
   elargi ensuite — c'est un reglage de courbe, pas une refonte.
   Le SVG est INCORPORE et non charge par une URL : `public/img/` n'existe plus
   dans ce front, et un masque introuvable ne casse rien visiblement, il annule
   simplement le decoupage. Un defaut muet est le pire des defauts. */
/* ⛔ `preserveAspectRatio="none"` EST INDISPENSABLE. Sans cet attribut, un SVG
   vaut `xMidYMid meet` : il conserve ses proportions et se centre dans la
   boite, au lieu de la remplir. Le masque etait donc mis en boite a lettres et
   laissait l'image transparente sur les cotes. Mesure sur un ecran 1903 x 991 :
   boite de 952 x 801, forme d'origine en 482 x 499, donc 774 px utiles et
   89,5 px de vide de chaque cote — l'image ne touchait plus le bord droit.
   Le defaut est MUET : rien dans la console, juste une image plus etroite. */
/* La MEME courbe, retournee horizontalement, pour les ecrans ou l'image se
   pose a gauche. Le miroir est applique DANS le SVG (une transformation sur le
   trace), et non par un `transform` CSS sur la boite : celui-ci retournerait
   aussi la photo qu'elle porte, et les visages se liraient a l'envers. */
export const DECOUPE_MIROIR = "url(\"data:image/svg+xml;base64,PHN2ZyBwcmVzZXJ2ZUFzcGVjdFJhdGlvPSJub25lIiB3aWR0aD0iNDgyIiBoZWlnaHQ9IjQ5OSIgdmlld0JveD0iMCAwIDQ4MiA0OTkiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIHRyYW5zZm9ybT0idHJhbnNsYXRlKDQ4MiwwKSBzY2FsZSgtMSwxKSIgZD0iTTYuNDQ2MDhlLTA2IDIyOC4xOTZDMC4wMTAyMzczIDMxOS40MDkgMTIuMTk4NiA0MTIuNjA3IDM0Ljg2MDMgNDk5SDQ4MlYwSDI4Ljg2NzZDOS4yNTYwMyA3MC42MDY2IDAuMDA4OTE3MDQgMTQ4LjUzMSA2LjQ0NjA4ZS0wNiAyMjcuOTY3Qy0yLjE0ODg2ZS0wNiAyMjguMDQzIC0yLjE0ODUyZS0wNiAyMjguMTIgNi40NDYwOGUtMDYgMjI4LjE5NloiIGZpbGw9ImJsYWNrIi8+Cjwvc3ZnPgo=\")"

export const DECOUPE = "url(\"data:image/svg+xml;base64,PHN2ZyBwcmVzZXJ2ZUFzcGVjdFJhdGlvPSJub25lIiB3aWR0aD0iNDgyIiBoZWlnaHQ9IjQ5OSIgdmlld0JveD0iMCAwIDQ4MiA0OTkiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik02LjQ0NjA4ZS0wNiAyMjguMTk2QzAuMDEwMjM3MyAzMTkuNDA5IDEyLjE5ODYgNDEyLjYwNyAzNC44NjAzIDQ5OUg0ODJWMEgyOC44Njc2QzkuMjU2MDMgNzAuNjA2NiAwLjAwODkxNzA0IDE0OC41MzEgNi40NDYwOGUtMDYgMjI3Ljk2N0MtMi4xNDg4NmUtMDYgMjI4LjA0MyAtMi4xNDg1MmUtMDYgMjI4LjEyIDYuNDQ2MDhlLTA2IDIyOC4xOTZaIiBmaWxsPSJibGFjayIvPgo8L3N2Zz4K\")"

/* L'IMAGE PLEINE HAUTEUR de l'apercu editorial. Elle sort du flux : elle doit
   toucher le bord droit et les deux bandeaux, alors que le conteneur central
   porte 170 px de marge laterale. Une marge negative marcherait au pixel pres
   sur un seul palier ; l'ancrage absolu tient a toutes les tailles.
   ⚠️ Suppose un ancetre `position: relative` — la racine du format le pose. */
/* ⛔ RESERVE AUX GRANDS ECRANS. Un bloc en `position: absolute` occupant
   `top/right/bottom: 0` et 100 % de large RECOUVRE TOUTE LA PAGE des que la
   mise en page se replie : sur un telephone, l'apprenant n'aurait vu qu'une
   photo, sans texte ni bouton. En dessous du palier « md », on revient donc au
   cadre carre empile, qui a fait ses preuves.
   La bascule ne peut pas etre une simple valeur CSS : c'est le CHOIX DU
   COMPOSANT qui change, pas un reglage. D'ou le petit crochet ci-dessous. */
/* ⛔ TOUJOURS FAUX DEPUIS LE 21/09/2026 : l'image en pleine hauteur etait la
   signature de la vue editoriale, abandonnee. Les planches posent un conteneur
   d'image CADRE (480 x 600), pas une image pleine hauteur. Le crochet est
   conserve le temps que le code mort soit retire des neuf formats, a
   comportement constant. */
export const useApercuPleineHauteur = () => APERCU_EDITO

/* ⛔ L'IMAGE CHANGE DE COTE D'UN ECRAN A L'AUTRE (choix d'Hicham, 09/09/2026).
   ⚠️ Ce n'est PAS un retour a l'ancien lecteur : chez lui le masque etait
      toujours a droite. C'est un parti pris nouveau, et il a un cout — quand
      l'image change de cote, le texte change de cote avec elle. C'est ce que
      Nawal reproche depuis le debut. Le compromis est assume : l'alternance
      est REGULIERE, un ecran sur deux, jamais aleatoire, donc l'oeil
      l'anticipe au lieu de la subir. A trancher sur le visuel.
   `imagePleine("droite")` reproduit exactement le reglage d'origine. */
/* De quel cote se pose l'image, ecran par ecran.
   ⚠️ DETERMINISTE, jamais aleatoire : deux visites du meme ecran doivent
      donner le meme resultat, sinon l'apercu de l'admin et la vue de
      l'apprenant divergeraient, et Nawal ne pourrait rien valider.
      On prend le rang de l'ecran DANS SA PARTIE : c'est ce que l'oeil suit
      quand il enchaine deux ecrans voisins. */
export const coteDeLImage = (actIndex) =>
  (((actIndex?.sequence ?? 0) % 2) === 1 ? "gauche" : "droite")

export const imagePleine = (cote) => ({
  ...IMAGE_PLEINE,
  ...(cote === "gauche"
    ? { right:"auto", left:0, WebkitMaskImage: DECOUPE_MIROIR, maskImage: DECOUPE_MIROIR }
    : {}),
})

export const IMAGE_PLEINE = {
  position:"absolute",
  top:0, right:0, bottom:0,
  width:"50cqw",
  backgroundPosition:"center center",
  backgroundRepeat:"no-repeat",
  WebkitMaskImage: DECOUPE,
  maskImage: DECOUPE,
  WebkitMaskSize:"100% 100%",
  maskSize:"100% 100%",
  WebkitMaskRepeat:"no-repeat",
  maskRepeat:"no-repeat",
  pointerEvents:"none",
  /* Un element positionne passe devant ses freres restes statiques : sans
     ceci, l'image recouvrait la pastille de partie, qui la precede pourtant
     dans le code. */
  zIndex:0,
}

/* ⛔ LE PANNEAU DES ECRANS SANS IMAGE (09/09/2026).
   Mesure au banc sur vingt ecrans de SOFT SKILLS SCAN B1, en 1440 x 900 :
   quand l'ecran porte une image, le titre commence a x = 668 et s'aligne a
   GAUCHE ; quand il n'en porte pas, il commence entre 170 et 545 et se
   CENTRE. Ecart moyen : 311 px, et la largeur varie de 133 px. Le titre change
   meme de place dans le DOM — au-dessus du bloc, et non dedans. C'est le grief
   de Nawal, « la position des textes varie trop et perd le user », et il porte
   sur 406 ecrans des six modules qu'elle a cites.

   L'ancien lecteur ne connaissait pas ce probleme : sa moitie illustree etait
   TOUJOURS occupee. Sans image, elle recevait `mask-clear.svg`, un degrade
   radial bleu nuit, et portait les propositions (`MaskQuizz`).

   On reprend ce degrade tel quel — meme teinte #01092E, meme opacite 0,53,
   meme centre decale — plutot que d'inventer une couleur : c'est l'univers que
   Nawal regrette, pas une invention. La geometrie, elle, est celle du nouveau
   design : la meme decoupe que les images pleine hauteur.

   Le texte reste sur l'autre moitie : sa lisibilite n'est pas en cause. */
export const DEGRADE_PANNEAU =
  "radial-gradient(ellipse 83% 130% at 83% 55%, rgba(1,9,46,0) 0%, rgba(1,9,46,0.53) 100%)"

/* `pastille` — CAUSE B, ordre vertical (21/09/2026). Aux paliers empiles la
   rangee est en `column-reverse` : l'ordre VU est l'inverse de l'ordre du DOM.
   Pour obtenir [image, pastille, contenu] a l'ecran, il faut donc ecrire
   [contenu, pastille, image] dans le DOM. La pastille passee ici se masque
   d'elle-meme a partir de `md`, ou c'est celle de la page qui vaut. */
/* ⛔ 30/09/2026 — LES DEUX MOITIES DE LA PLANCHE ILLUSTREE. CAUSE COMMUNE DES
   POINTS 4, 5, 7 ET 8 DE LA FICHE DE NAWAL, et ce n'est pas un reglage de
   valeur : c'est la STRUCTURE qui etait fausse.
   Ce bloc centrait UN GROUPE (image + gouttiere + contenu) dans la place
   disponible. La maquette pose DEUX MOITIES FIXES et met chaque chose DANS la
   sienne. Tant que c'est un groupe centre, la position du contenu DEPEND de
   l'image — c'est le point 8, mot pour mot : « l'image deplace le conteneur de
   reponse » — et la bande basse suit le contenu au lieu de remplir sa moitie
   (point 7).
   Releve sur les planches Desktop 04, 06' et 10 du fichier « Claude », trois
   fois les memes cotes :
     moitie gauche   0 -> 691   (45,7011 % de 1512) — nodes 3:181372 (06'),
                                3:100868 (04) et 4:606 (10), les trois a 691 ;
                                l'image de 480 x 600 y est centree, donc a 105,5
                                (nodes 3:100869 et 4:607)
     moitie droite 691 -> 1512  (54,2989 %) — nodes 3:181374, 3:100870 et 4:560,
                                les trois a 821 ; le bloc de 741 y est centre,
                                donc a 731 (nodes 3:181380, 3:100876, 4:566)
   Mesure du rendu AVANT, dans Chrome a 1512 (filet) : image a 126 sur Texte et
   Saisie, a 80 sur le Quiz ; bloc de texte a 646 au lieu de 731 ; bande basse
   de 646 a 1387 au lieu de 691 a 1512. Les LARGEURS etaient bonnes : c'est la
   POSITION qui etait fausse, et differemment sur chaque format. C'est ce que
   produit une regle qui depend du contenu au lieu de la planche.

   ⛔ PREMIERE TENTATIVE, RETIREE, ET LA LECON TIENT EN TROIS LIGNES. Elle
   posait les moities par un selecteur `& > *` sur la rangee. Mesure au filet :
   l'image passait de 480 a 578 px (`flex-basis` prime sur la `width` que pose
   `ImageCarree`), `quiz-image` debordait jusqu'a 1543 pour une planche de 1512,
   et `:first-of-type` ne veut pas dire « premier enfant » quand les enfants
   sont de types differents. On ENVELOPPE donc chaque moitie au lieu d'imposer
   quoi que ce soit aux enfants : ils gardent leurs propres largeurs.

   ⚠️ `display:"contents"` AUX DEUX PALIERS EMPILES, ET C'EST LE POINT DELICAT.
   L'enveloppe doit disparaitre de la mise en page quand la composition s'empile,
   sinon elle ajoute un niveau de flex la ou il n'y en avait pas. `contents` la
   rend inerte : les enfants redeviennent enfants directs de la rangee,
   exactement comme avant ce lot. Rien ne change en Mobile ni en Tablet.
   ⛔ SAUF EN FENETRE COURTE (760-1129 px, palier `sm`) : le bloc y passe en
   rangee, et une enveloppe inerte y faisait de la pastille une troisieme
   colonne (B85, 04/10). La moitie y redevient une boite : voir `Moitie`.
   ⚠️ Pas d'enveloppe du tout en `horsFlux` (apercu) : l'image y est ancree hors
   du flux, la rangee n'a plus qu'un enfant, et l'enveloppe fausserait le
   centrage — c'est le piege documente plus bas, ou je suis deja tombe. */
export const MOITIE_IMAGE   = "45.7011cqw"   /* 691 / 1512 */
export const MOITIE_CONTENU = "54.2989cqw"   /* 821 / 1512 */

const Moitie = ({ largeur, etire, children }) => (
  <Box sx={{
    display:["contents", "contents", "flex", "flex"],
    flex:[null, null, `0 0 ${largeur}`, `0 0 ${largeur}`],
    minWidth:0,
    flexDirection:"column",
    /* Le contenu se centre DANS sa moitie : l'image de 480 tombe a 105,5 dans
       les 691, le bloc de 741 a 731 dans les 821. Aucune valeur posee a la
       main : ce sont les moities qui les placent. */
    alignItems:"center",
    /* La moitie de CONTENU empile depuis le haut et reserve le retrait de la
       planche (la pastille y est a 195, node 3:100872, soit 120 px sous le haut
       de la colonne). Celle de l'IMAGE centre, puisque l'image y flotte
       (node 3:100869, y=229,5 dans une colonne de 77 a 982). */
    justifyContent: etire ? "flex-start" : "center",
    paddingTop: etire ? [null, null, "120px", "120px"] : undefined,
    /* ⚠️ `ImageCarree` porte `alignSelf:"flex-start"` aux deux grands paliers, et
       ce n'etait pas un choix de position : c'etait un garde-fou contre le
       `stretch` de la RANGEE, qui etirait le carre (« la position varie trop »,
       grief de Nawal). Dans une moitie en colonne, l'axe transversal est
       HORIZONTAL : ce meme `flex-start` collait l'image au bord gauche, mesure
       a x=0 au lieu de 105,5. On le neutralise ICI, et seulement ici — la
       moitie ne s'etire plus, le garde-fou n'a plus d'objet.
       `null` aux deux paliers empiles : rien n'est emis, donc rien ne bouge la
       ou l'enveloppe est deja inerte (`display:"contents"`). */
    "&& > *" : { alignSelf:[null, null, "center", "center"] },
    /* ⛔ 01/10/2026 — SEUL LE DERNIER ENFANT GRANDIT, ET C'EST LA LECON DU MATIN.
       Un premier essai posait `flex:1` sur `& > *`, donc sur TOUS les enfants de
       la moitie — pastille comprise. Mesure : la pastille prenait 472 px et la
       colonne tombait a 436, la bande finissant 13 px SOUS le bas de l'ecran.
       La pastille garde sa taille, la colonne prend ce qui reste. */
    /* ⛔ 01/10/2026 — PAS DE `width:"100%"` ICI, ET C'EST LA CAUSE DU POINT 1
       D'HICHAM. Je l'avais ajoute hier avec `flex:1` pour que la colonne prenne
       la hauteur restante. Mais `&&` double la specificite : ce `100%` ECRASAIT
       le `LARGEUR_CONTENU` (min(49cqw, 741px)) que les formats posent eux-memes,
       et que j'avais mesure le 21/09 sur le node 3:181380.
       Mesure du 01/10 a 1512 : titre du Format texte illustre a x=691 large de
       821, la ou la planche 06' le pose a x=731 large de 741. Le Quiz y
       echappait par accident — sa colonne n'est pas le DERNIER enfant de la
       moitie — d'ou TROIS formats avec TROIS largeurs pour le meme bloc.
       `flex:1` suffit pour la hauteur ; la largeur appartient a l'appelant. */
    ...(etire ? { "&& > *:last-child": { flex:1, minHeight:0 } } : {}),
    /* ⛔ 04/10/2026 — B85 : EN FENETRE COURTE, LA MOITIE REDEVIENT UNE BOITE.
       Cette regle tombe dans le palier `sm`, ou la moitie est `contents` : le
       bloc passait bien en rangee, mais la pastille en devenait un enfant
       direct, donc une TROISIEME colonne entre l'image et la question (photos
       b85, 800 x 500 et 1024 x 640, tous les ecrans illustres), et la rangee
       debordait de l'ecran des deux cotes (-10 / 810 sur le quiz). La moitie de
       contenu prend la place qui reste ; celle de l'image garde la sienne. */
    [FENETRE_COURTE]: {
      display:"flex",
      flex: etire ? "1 1 0" : "0 0 auto",
    },
  }}>
    {children}
  </Box>
)

/* ⛔ LES MOITIES NE VALENT QUE S'IL Y A UNE IMAGE, ET C'EST MESURE.
   Premier essai : les moities posees sur TOUS les ecrans illustres. Le releve
   avant/apres a montre que les ecrans SANS image y passaient aussi — ils
   empruntent ce bloc pour le panneau — et que leur contenu partait dans la
   moitie droite : carte de texte de 456 a 801, question de quiz de 256/1000 a
   731/741, propositions de 362 a la moitie droite. Or la planche 08 (quiz sans
   image) centre tout sur la PLANCHE : question a 256 large de 1000,
   propositions a 381 larges de 750. Sans image, il n'y a pas deux moities.
   `image` est la seule donnee qui decide, et c'est deja elle qui decide du
   reste (`aUneImage` chez les appelants). */
export const BlocIllustre = ({ image, children, sx, horsFlux, pastille, remplitTablette }) => {
  const { [FENETRE_COURTE]: sxCourte = {}, ...sxReste } = sx || {}
  const enMoities = !horsFlux && !!image
  return (
  <Box sx={{
    display:"flex",
    /* ⚠️ L'ORDRE DU CODE EST INVERSE DE L'ORDRE VU. Le contenu vient d'abord
       dans le code, l'image ensuite, et `row-reverse` remet l'image a gauche.
       Un format qui place son image en premier et copie `row-reverse` obtient
       l'image A DROITE : c'est arrive. On passe donc TOUJOURS l'image par la
       propriete `image`, jamais dans les enfants. */
    /* ⛔ `column-reverse` AUX PALIERS EMPILES, ET C'EST MESURE SUR LAB.
       Le contenu est ecrit AVANT l'image dans le DOM (voir la note ci-dessus) :
       en simple `column`, l'image se retrouvait donc EN BAS de l'ecran, apres
       les reponses. Les planches Tablet et Mobile la mettent EN HAUT, avant la
       question — captures LAB a 834x1194 et 393x855 : image en bas, question
       coupee en pleine phrase par la bande, quatrieme carte tronquee.
       `column-reverse` retablit l'ordre vu sans toucher a l'ordre du DOM, donc
       sans casser `row-reverse` du Desktop ni l'ordre de lecture. */
    /* ⛔ L'IMAGE D'ABORD, LE TEXTE ENSUITE — TOUJOURS (consigne d'Hicham,
       21/09/2026). Le bloc rendait le contenu AVANT l'image dans le DOM et
       comptait sur `-reverse` pour remettre l'image devant. Ca marche tant que
       la direction est inversee — mais la branche `horsFlux` pose un `column`
       SIMPLE, et l'image repassait alors sous le texte. Une inversion visuelle
       qui depend d'une autre regle finit toujours par se retourner.
       L'image est donc ecrite EN PREMIER dans le DOM, et les directions ne sont
       plus inversees. Le rendu est identique — image a gauche en deux colonnes,
       image au-dessus une fois empile — mais il ne depend plus d'une astuce, et
       l'ordre de lecture d'un lecteur d'ecran devient correct.
       Conforme aux planches : les HUIT plaques Desktop illustrees posent le bloc
       image a x=0, et en Tablet il est a y=0 avec le texte a y=460. */
    flexDirection:["column", "column", "row", "row"],
    justifyContent:"center",
    alignItems:["center", "center", "stretch", "stretch"],
    /* La gouttiere ne separe plus rien aux deux grands paliers : ce sont les
       deux moities qui se touchent, et chacune centre son contenu. La garder
       ajouterait 40 px entre deux moities deja jointives, et 691 + 40 + 821
       ne tient pas dans 1512. Elle reste aux paliers empiles, ou la
       composition est bien une pile de deux blocs. */
    gap: enMoities ? ["16px", "20px", 0, 0] : ["16px", "20px", "32px", "40px"],
    /* ⛔ 04/10/2026 — EMPILE, 40 px ENTRE L'IMAGE ET LA PASTILLE EN MOBILE, 50
       EN TABLET (planches 04, 05, 06' : 3:80828 et 3:100936, `pt-40` / `pt-50`
       de la zone sous l'image), et toujours 20 entre la pastille et l'enonce.
       Pas par le `gap` : les deux moities sont en `display: contents` aux
       paliers empiles, donc la pastille et la colonne sont des enfants directs
       de ce bloc, et un `gap` de 50 s'appliquait aussi entre elles (vu au
       comparateur). La marge va sous l'image seule. */
    /* 22,6 = 40 - 16 (le `gap` Mobile) - 1,4 mesure au comparateur (le cadre
       de l'image tombe sur une demi-unite) ; 30 = 50 - 20. Pas en fenetre courte,
       ou la composition repasse en rangee. ⚠️ Vise l'enfant de la moitie image,
       qui est `display: contents` aux paliers empiles : a revoir si `Moitie`
       change de structure. */
    ...(enMoities ? { "& > :first-of-type > *": { marginBottom:["22.6px", "30px", 0, 0] } } : {}),
    /* ⚠️ LA RANGEE REPREND LA LARGEUR DE LA PLAQUE, pas celle du conteneur.
       `cqw` se rapporte a la plaque (c'est deja l'unite de LARGEUR_IMAGE et de
       LARGEUR_CONTENU) ; les marges negatives la font ressortir des gouttieres
       laterales du conteneur central, QUELLES QU'ELLES SOIENT. C'est ce qui
       explique qu'un meme groupe centre tombait a 126 sur le Texte et a 80 sur
       le Quiz : les deux n'ont pas le meme conteneur. */
    ...(enMoities ? {
      /* Tablet aussi (04/10/2026) : le bloc remplit la hauteur, pour que la
         bande se colle au bas comme sur la planche (3:100944). En Mobile la
         page defile, le bloc suit son contenu. */
      /* Tablet : seulement pour l'ecran dont la bande se cale en bas (la saisie,
         `remplitTablette` ; le quiz le regle dans son propre `sx`). Sur l'ecran de texte, la carte s'etirait
         jusqu'au bas de l'ecran, vide (photos du 04/10).
         `auto`, pas 0 : le bloc grandit jusqu'au bas de l'ecran mais ne se
         comprime pas sous son contenu ; sur une fenetre basse (telephone en
         paysage), c'est la page qui defile au lieu de rogner le bouton
         (bouton-dans-le-cadre.mjs, 667 x 375, deja vrai sur LAB). */
      flex:[null, remplitTablette ? 1 : null, 1, 1],
      minHeight:[null, remplitTablette ? "auto" : null, 0, 0],
      width:["100%", "100%", "100cqw", "100cqw"],
      marginLeft:[0, 0, "calc((100cqw - 100%) / -2)", "calc((100cqw - 100%) / -2)"],
      marginRight:[0, 0, "calc((100cqw - 100%) / -2)", "calc((100cqw - 100%) / -2)"],
    } : { width:"100%" }),
    /* ⛔ 01/10/2026 (nuit) — PLUS DE MARGE AU-DESSUS DU CONTENU, SAUF EN MOITIES.
       Elle s'AJOUTAIT aux 20 px que la pastille pose deja sous elle : 40 px
       entre pastille et enonce en Desktop, 35 en Tablet. Releve sur les 66
       planches qui ont une pastille, toutes sections et tous paliers : l'ecart
       entre la pastille et ce qui la suit vaut 20 px, sans une exception (ex.
       3:60539 et 3:60566, `itemSpacing` 20 ; releve dans figma-planches.json).
       Corrige ici, et non format par format, pour que l'enonce reste a la meme
       hauteur d'un ecran a l'autre (point 6 de la revue de Nawal). */
    /* 04/10/2026 : plus de marge non plus au-dessus de l'IMAGE empilee — elle
       decalait l'image de 10 / 15 px sous le haut de la planche (comparateur,
       section 04 : 3:80849, 3:100934). */
    marginTop: 0,
    /* ⛔ EN APERCU, LA COLONNE SE FERRE A GAUCHE. L'image etant sortie du flux
       (ancrage absolu), la rangee ne contient plus qu'elle : `center` la posait
       au milieu de l'ecran, donc a moitie SOUS la photo. Mesure : le texte
       commencait a 260 px au lieu de 170 et se faisait couper. */
    /* ⚠️ ET SURTOUT PAS `justifyContent: flex-start` : la rangee est en
       `row-reverse`, ou `flex-start` designe la DROITE. Essaye, mesure : la
       colonne se posait sous la photo et le texte disparaissait entierement.
       C'est le piege signale quinze lignes plus haut, dans lequel je suis
       retombe. On sort donc du sens inverse : l'image etant hors flux, la
       rangee n'a plus qu'un enfant, une simple colonne suffit et le doute
       disparait avec elle. */
    /* ⚠️ Uniquement quand l'image est REELLEMENT hors flux. En apercu, les
       images `contain` — 114 ecrans du catalogue — gardent leur cadre carre
       DANS la rangee : leur imposer une colonne les empilerait sous le texte
       alors qu'il y a la place de les mettre a cote. La condition porte donc
       sur ce que fait l'image, pas sur le mode d'apercu. */
    /* ⛔ SURTOUT PAS `position: relative` ICI. Ce bloc vit A L'INTERIEUR des
       170 px de marge du conteneur central : le rendre positionne en fait
       l'ancre de l'image, qui s'arrete alors 170 px avant le bord de l'ecran
       et ne fait plus que la hauteur du contenu. Mesure : image large de 952 px
       s'arretant a 1733 au lieu de 1903, et haute de 387 px au lieu de la zone
       entiere. L'ancre doit rester le conteneur central. */
    ...(horsFlux ? { flexDirection:"column",
                     alignItems: horsFlux === "gauche" ? "flex-end" : "flex-start",
                     justifyContent:"flex-start", marginTop:0 } : {}),
    ...sxReste,
    /* Le `[FENETRE_COURTE]` de l'appelant s'AJOUTE a celui-ci au lieu de le
       remplacer (meme cle dans le meme objet : le dernier gagnait tout). */
    /* ⛔ 04/10/2026 — B85 : EN RANGEE SEULEMENT S'IL Y A DEUX MOITIES. Sans image
       il n'y a rien a mettre a cote : la rangee posait la pastille A GAUCHE de
       la question (quiz, saisie : photos b85, 1024 x 640, deja vrai sur LAB).
       Sans image, le bloc garde l'empilement de son palier. */
    [FENETRE_COURTE]: {
      /* `enMoities` et non `image` : en apercu (`horsFlux`) l'image est hors
         du flux et il n'y a pas de moities ; une rangee y remettrait la
         pastille a cote du contenu (relecture du 04/10). */
      ...(enMoities ? { flexDirection:"row", alignItems:"stretch" } : {}),
      /* (voir la marge sous l'image, plus haut : sans objet en rangee) */
      ...(enMoities ? { "& > :first-of-type > *": { marginBottom:0 } } : {}),
      ...sxCourte,
    },
  }}>
    {enMoities
      ? <>
          <Moitie largeur={MOITIE_IMAGE}>{image}</Moitie>
          {/* ⛔ 30/09/2026 — POINT 4 DE LA FICHE DE NAWAL : « le sous-chapitre
              n'est pas rattache au bloc auquel il appartient, il est centre dans
              la page ». C'etait exact. La pastille etait rendue par le conteneur
              central, donc centree sur la PLANCHE (756), pendant que la question
              vit dans la moitie droite. La maquette la pose DANS la colonne, au-
              dessus de la question : nodes 3:181376 (06'), 3:100872 (04) et
              4:562 (10), dont le centre tombe a 1101,5 — le centre de la moitie
              de contenu ET celui du bloc question. Elle entre donc dans la
              moitie, et c'est la moitie qui la place. */}
          <Moitie largeur={MOITIE_CONTENU} etire>{pastille}{children}</Moitie>
        </>
      : <>{image}{pastille}{children}</>}
  </Box>
  )
}

/* L'image de gauche des variantes illustrees. Un seul endroit pour la regle,
   sinon elle diverge d'un format a l'autre — c'est exactement ce qui s'est
   produit. */
/* ⛔ PORTRAIT 4:5, ET C'EST LE FIGMA 2026 QUI LE DIT (arbitrage A1, 21/09/2026).
   Le defaut etait `1 / 1`, herite de la fiche de revue apprenant du 04/09 qui
   affirmait « image CARREE a gauche ». Les planches 2026 disent l'inverse :
   node 4:559, colonne d'image `p-[80px]`, image `max-w-[480px] max-h-[600px]`,
   soit un portrait 4:5 — et les planches 04, 05 et 06' montrent la meme forme.
   Le quiz illustre passait deja ce ratio en explicite ; on generalise SON
   comportement au lieu d'en inventer un autre.
   ⚠️ `type` (contain / cover) n'est PAS touche : c'est une donnee du contenu,
   mesuree a 424 `cover` et 124 `contain` sur les 74 modules de LAB. Le cadre
   change, la decision d'ajustement reste au contenu. */
/* `bordABord` — CAUSE C (21/09/2026). En Mobile les planches 04, 05, 06' et 09
   montrent le media BORD A BORD, sans gouttiere. Le conteneur central en pose
   une de 25 px a ce palier, sauf pour les formats qui passent deja
   `sansMargesLaterales` (quiz, questionnaire) : mesure LAB, image a 393 px
   pour eux, 343 px pour les autres. On ne retire donc PAS la gouttiere du
   conteneur — le texte en a besoin — on fait sortir la seule image, et
   uniquement quand l'appelant dit qu'il y a une gouttiere a franchir. */
/* ⛔ 01/10/2026 — L'IMAGE ENTIERE, TOUJOURS. DECISION D'HICHAM, mot pour mot :
   « Image entiere toujours s'il te plait. »
   Elle remplace celle du 30/09 (« remplies c'est mieux... tu zoom au max »).
   Les deux ne pouvaient pas tenir ensemble : dans un cadre portrait 4:5, une
   photo large perd forcement ses bords, et c'est ce rognage qu'il a vu le 01/10
   (« image trop zoome »). Entre « ca remplit » et « on voit tout », il tranche
   pour « on voit tout ».
   LA REGLE ECRASE LE CONTENU, comme pour les libelles de bouton (point 1.3 de
   la fiche V2) : l'interrupteur « afficher / elargir » de l'editeur ne decide
   plus du rognage. Il est donc INERTE pour l'apprenant.
   ⛔ EN SUSPENS POUR HICHAM : faut-il retirer cet interrupteur de l'editeur ?
   Le laisser visible alors qu'il ne fait plus rien est un piege pour Nawal.
   `type` reste dans la signature pour ne pas casser les appelants, et pour que
   le jour ou la decision bouge, on sache ou elle se branchait. */
export const ImageCarree = ({ lien, type, ratio = "4 / 5", cadre, largeur, bordABord }) => (
  <Box sx={{
    width: bordABord
      ? [`calc(100% + ${GOUTTIERE_MOBILE * 2}px)`, ...(largeur || LARGEUR_IMAGE).slice(1)]
      : (largeur || LARGEUR_IMAGE),
    ...(bordABord ? { marginLeft:[`-${GOUTTIERE_MOBILE}px`, 0, 0, 0], marginRight:[`-${GOUTTIERE_MOBILE}px`, 0, 0, 0] } : {}),
    /* ⛔ LE PORTRAIT DOIT TENIR DANS LA FENETRE, ET C'EST MESURE.
       Passer de 1:1 a 4:5 rend le cadre 25 % plus haut. Mesure sur LAB a
       1512 x 700, apercu apprenant : 04/05/06' sortaient de 58 px
       (420 x 525, bas a 758 pour 700 de haut) et le quiz illustre de 133 px
       (480 x 600, bas a 833) — ce dernier debordait DEJA avant ce lot, il
       portait deja le 4:5.
       ⚠️ On ne borne PAS par `maxHeight` : avec une largeur fixee et une
       hauteur bornee, le navigateur ecrase le cadre et le ratio part (lecon
       du cadre video, plus haut dans ce fichier). On borne la LARGEUR, et la
       hauteur suit le ratio : largeur <= hauteur disponible x 4/5.
       Le retrait de 260 px est mesure, pas devine : le haut du cadre est a
       233 px du bord (en-tete, pastille, marges) aux deux hauteurs testees,
       plus une respiration de 27 px. */
    /* ⚠️ Le plafond du lot precedent annulait le bord a bord : `maxWidth:100%`
       ramenait la largeur a celle du conteneur, gouttiere comprise. Mesure :
       343 px au lieu de 393 attendus. Au palier Mobile, une image qui doit
       franchir la gouttiere n'a donc pas de plafond. */
    maxWidth:[bordABord ? "none" : "100%", "100%",
              "min(100%, calc((100cqh - 260px) * 0.8))",
              "min(100%, calc((100cqh - 260px) * 0.8))"],
    /* ⛔ LE RATIO DOIT TENIR AUX PALIERS EMPILES — CAUSE A (21/09/2026).
       Avant : `width:100%` aux paliers xs/sm PLUS un plafond de hauteur. Le
       plafond gagnait et `aspect-ratio` etait ignore. Mesure sur LAB :
       tablette 834x310 = ratio 2,69 ; mobile 393x250 = ratio 1,57, au lieu de
       0,80. Le meme calcul avec l'ancien 1:1 donnait EXACTEMENT ces valeurs :
       l'ecart est donc ANTERIEUR au passage en 4:5, pas une regression.
       Les planches ne demandent pas le meme cadre partout, et c'est mesure sur
       elles : Tablet garde le PORTRAIT 4:5 dans un cadre contenu (planche 04 :
       318 x 404 sur 834, soit 0,79) ; Mobile passe en bande PAYSAGE pleine
       largeur (planche 04 : ratio 1,26 ; planche 09 : 1,25), soit 5:4. */
    /* ⛔ 01/10/2026 — « AFFICHER L'IMAGE » NE LAISSE PLUS DE BANDES VIDES.
       Constat d'Hicham sur LAB : « certaines images sont coupées, il faudrait
       mieux gérer ces images-là ». Ce sont les illustrations (le cerveau en PNG,
       fond blanc) : dans un cadre 4:5 impose, `cover` les rogne en haut et en bas.
       Le levier existe deja — chaque ecran choisit « elargir » (cover) ou
       « afficher » (contain) dans l'editeur — mais `contain` etait inutilisable :
       le cadre gardait son 4:5, donc l'image whole flottait entre deux bandes
       vides. Le commentaire du `objectFit` le disait sans en tirer la
       consequence : « contain laisserait des bandes vides ».
       Desormais, en mode « afficher », LE CADRE PREND LA FORME DE L'IMAGE : plus
       de bandes, plus de rognage, et l'image aussi grande que sa colonne le
       permet — « zoom au max sans deformer », mot pour mot ce qu'Hicham demande.
       ⚠️ Le mode « elargir » (cover) NE BOUGE PAS : il garde le cadre 4:5 des
       planches et remplit, ce qu'Hicham prefere pour les photos. Les deux modes
       deviennent enfin deux vrais choix, au lieu d'un choix et d'un repoussoir. */
    /* Plus de cadre impose : il prend la forme de l'image, donc ni bande vide
       ni rognage. Voir la note au-dessus du composant. */
    aspectRatio: "auto",
    ...(cadre ? { margin: cadre } : {}),
    /* ⛔ SANS CECI, L'IMAGE N'EST PAS CARREE. Le conteneur aligne ses enfants
       en `stretch` : l'image, etant un element du flex, se fait etirer a la
       hauteur de la rangee, et `aspect-ratio` cede — mesure au navigateur,
       1440x900 : 401 x 572 sur un ecran de texte, 401 x 410 sur un quiz. La
       hauteur de la photo suivait donc la longueur du texte a cote d'elle.
       C'est exactement le grief de Nawal, « la position varie trop » : deux
       ecrans jumeaux, deux photos de hauteurs differentes.
       `flex-start` sort l'image de l'etirement. Elle redevient un carre franc,
       identique partout, ancre en haut comme la colonne de texte. */
    /* ⚠️ CENTREE UNE FOIS EMPILEE (21/09/2026, capture reelle sur LAB).
       En Tablet l'image etait collee au bord gauche et rognee, alors que la
       planche la centre : node Rectangle 143151 a x=257 sur une plaque de 834,
       soit (834 - 320) / 2 exactement. En deux colonnes elle reste ancree en
       haut a gauche de SA colonne, ce qui est le comportement voulu. */
    alignSelf:["center", "center", "flex-start", "flex-start"],
    /* ⛔ Constat du 07/09, fenetre 1024 x 640 puis telephone 390 x 844. Une
       fois la double colonne repliee, l'image occupe toute la largeur ; un
       carre fait alors 390 px de haut sur un telephone, et la question comme
       les propositions tombent sous la ligne de flottaison. On voyait une
       photo et un bouton desactive, rien d'autre. La hauteur suit donc la
       fenetre tant que le bloc est empile, et redevient un carre franc des
       que les deux colonnes reviennent. */
    /* Le plafond reste un FILET DE SECURITE, il ne doit plus dicter la forme.
       Calibre pour ne pas mordre sur le ratio voulu : mobile 393 de large en
       5:4 fait 314 px (plafond 42cqh = 359 a 855 de haut) ; tablette 318 de
       large en 4:5 fait 398 px (plafond 40cqh = 478 a 1194 de haut). */
    maxHeight:["min(360px, 42cqh)", "min(480px, 40cqh)", "unset", "unset"],
    /* ⛔ QUI CEDE QUAND CA NE RENTRE PAS. Mesure au banc, 1024 x 640 : le bloc
       des propositions tombait a 47 px de haut pour 336 px de contenu. En
       `flexShrink: 0` l'image ne cede jamais, et c'est le texte — la seule
       chose dont l'apprenant a besoin — qui encaisse tout.
       Une fois la pile formee, c'est donc l'image qui se comprime ; en deux
       colonnes elle reste intacte, la hauteur n'y est plus disputee. */
    flexShrink:[1, 1, 0, 0],
    minHeight:["120px", "120px", "unset", "unset"],
    [FENETRE_COURTE]: {
      width:"min(34cqw, 340px)",
      /* borne le carre a la hauteur de la rangee : sans elle il la depassait
         de 5 px et se faisait rogner par l'`overflow: hidden` du quiz */
      maxHeight:"100%",
      minHeight:"unset",
      flexShrink:0,
    },
    /* ⛔ PLUS DE BORDURE. Aucune planche 2026 n'en porte : ni 04, ni 05, ni 06',
       ni 09, ni 10. Le trait noir venait du lecteur historique. */
    overflow:"hidden",
  }}>
    {/* ⚠️ `Box component="img"` ET NON UN `<img style={{}}>`, ET C'EST MESURE.
        Une propriete que jsdom ne connait pas — `aspect-ratio` en est une —
        est SILENCIEUSEMENT PERDUE quand on la pose en style INLINE : le banc
        rend le HTML dans jsdom, et l'attribut `style` serialise ne la contient
        pas. Mesure du 01/10 : cadre a 480 x 0, `aspectRatio` calcule a `auto`.
        Emotion, lui, ecrit une vraie classe CSS, que jsdom recopie telle quelle
        et que Chrome applique. Meme famille que le piege deja paye le 01/10 au
        matin (`getComputedStyle(...).aspectRatio` qui rend `undefined` en
        jsdom) : ce que jsdom ignore, il le mange. */}
    <Box
      component="img"
      src={lien}
      alt=""
      sx={{
        width:"100%",
        /* La hauteur suit l'image : le cadre l'epouse, donc ni bande vide ni
           rognage. Voir la note au-dessus du composant. */
        height: "auto",
        /* ⛔ `auto 4 / 5`, ET LE MOT `auto` FAIT TOUT LE TRAVAIL. Il dit au
           navigateur : « prends le format NATUREL de l'image, et seulement si
           elle n'en a pas encore, prends 4/5 ».
           Sans lui, une image pas encore chargee — ou introuvable — a une
           hauteur de ZERO : mesure au banc le 01/10, cadre a 480 x 0 sur les
           trois ecrans illustres. La page sauterait a chaque chargement, et une
           adresse cassee ferait disparaitre le bloc au lieu de laisser un vide
           visible. Le 4/5 est le format des planches (node 3:181373, 480 x 600),
           donc la reserve a exactement la bonne taille. */
        aspectRatio:"auto 4 / 5",
        /* Garde-fou de hauteur : une image TRES haute (un portrait 2:3 fait 720
           px de haut pour 480 de large) sortirait de la colonne. Le plafond
           reprend celui de la largeur, 260 px de chrome retires de la hauteur du
           conteneur. Avec `contain`, l'image y reste entiere — elle laisse alors
           un peu de place sur les cotes plutot que d'etre rognee, ce qui est
           exactement l'arbitrage d'Hicham du 01/10. */
        maxHeight:"calc(100cqh - 260px)",
        objectFit: "contain",
        display:"block",
      }}
    />
  </Box>
)

// Lot A (18/09) : progression de sequence (avancement dans le chapitre courant),
// en %. Extraite de l'ancien BottomBar pour alimenter la barre desormais placee
// SOUS LE HEADER (TopBar), conforme aux frames NEW ou la progression quitte le
// bandeau bas. Pure et testable.
export function progressionSequence(actFormation, actIndex) {
  const actChapitre = actFormation?.chapitre?.[actIndex?.chapitre]
  if (!actChapitre) return 0
  let total = 0, faites = 0
  ;(actChapitre?.partie || []).forEach((partie) => {
    ;(partie?.sequence || []).forEach((seq) => {
      total += 1
      if (seq?.isDone) faites += 1
    })
  })
  if (total <= 0) return 0
  return (faites / total) * 100
}

// Lot A (18/09) : les frames NEW n'ont PAS d'ancien bandeau bas (nom de module +
// « Sequence % » + jauge « Module % » + « ecran N sur M »). Ces reperes vivent
// desormais dans le Plan de formation, et la progression de sequence passe sous
// le header (TopBar). BottomBar ne porte plus que le CTA, aligne en bas a droite
// dans la bande basse claire des ecrans de contenu.

/* ⛔ LOT G — TOUT EST OPT-IN ICI, ET LE DEFAUT EST NEUTRE.
   `fond`, `hauteur` et `colonne` viennent du FORMAT, parce que la composition
   depend de lui (cf. le tableau de mesures en haut de fichier).
   Sur les trois ecrans de question mesures — quiz 3:282231, questionnaire
   3:241965, champ de saisie 3:40519 — la rangee de controles est une colonne de
   750 px centree, et le CTA se cale sur SON bord droit (x=1131 sur 1512), pas
   sur le bord de l'ecran. Ces trois-la demandent donc `colonne`.
   Un premier jet avait fait l'inverse : fond teinte par defaut, et une colonne
   centree de 750 px imposee a tous. Consequence non voulue, car `BottomBar` est
   partagee par SEPT formats : le bouton du Diagnostic et celui de la video, que
   Figma pose contre le bord droit, se retrouvaient ramenes vers le centre. Un
   defaut qui modifie des ecrans qu'on n'a pas mesures est un defaut faux.
   Ici : bouton a droite, fond transparent, hauteur minimale de 85 px. Les trois
   formats dont la frame montre reellement une bande la demandent explicitement
   (`fond` / `hauteur`), les autres n'ont rien a declarer. */
/* ⛔ LOT H — LA BANDE DE REPONSE, TELLE QUE FIGMA L'ENCODE (et non telle que
   je l'avais deduite au lot G).
   Le `get_design_context` des quatre planches de question dit la meme chose, en
   toutes lettres, et c'est l'inverse de ce que faisait le code :

     zone de contenu : flex column, justify-between
     zone haute      : flex-[1_0_0], min-h-px        -> elle prend le RESTE
     bande           : h-[444px] min-h-[400px] shrink-0, px-60 py-30, w-full
                       flex column, items-center, justify-between
                       |- bloc de reponses      w-[750px]
                       |- rangee de controles   w-[750px] justify-between

   Trois consequences, toutes verifiees au node :
   1. La bande a une hauteur FIXE et c'est la zone haute qui absorbe le reste.
      Le lot G faisait l'inverse (`flex:1` sur la bande), d'ou une bande de
      653 px mesuree sur LAB la ou la planche en donne 444.
   2. Le CTA est DANS la bande, dans sa rangee de controles. Il n'y a pas de
      barre du bas separee sur ces ecrans : `BottomBar` n'a rien a y faire.
   3. `h-444` vaut pour le QCM, qui porte quatre cartes ; `h-400` pour le
      questionnaire, le Vrai/Faux et la saisie. Le minimum est 400 dans tous les
      cas.

   ⚠️ CE QUI PRECEDE DECRIT LA PLANCHE, PAS LE CODE, SUR UN POINT : Figma pose
   `shrink-0` sur la bande, le code pose `flexShrink:1`. La raison est ecrite au
   ras du code plus bas — avec deux blocs incompressibles, c'est le bas de
   l'ecran qui sort du cadre, bouton de validation compris. La planche n'a pas ce
   probleme parce qu'elle n'a qu'une seule taille.

   ⚠️ Les hauteurs sont posees a partir du palier `md`, et en Tablet pour les
   formats qui le demandent (`hauteurFixeTablette` : la saisie, planches Tablet
   a 400, 3:60574 ; le questionnaire depuis le 03/10, 3:282078). Ailleurs sous `md`, la bande epouse son contenu.
   Ce commentaire disait que Tablet et Mobile « ne sont pas figes chez
   Valentin » : les planches Tablet et Mobile existent bien (corrige le
   02/10/2026). */
/* `min(<valeur de la planche>, N cqh)` : la valeur Figma tant que la fenetre
   est au moins aussi haute que la maquette, une fraction du conteneur en
   dessous. 46 % correspond au rapport 444/982 de la planche du quiz, arrondi au
   point superieur pour que 982 px rende exactement 444. */
const hplafond = (v, pc = 46) => (typeof v === "string" && v.endsWith("px") ? `min(${v}, ${pc}cqh)` : v)

/* ⛔ LE PLANCHER DE LA BANDE — LA SEULE VALEUR QUI DECIDE VRAIMENT.
   J'ai d'abord cru que la priorite de compression se reglait par un facteur
   (`flexShrink` 2 contre 1). Mesure : elle ne se regle pas du tout par la.
     — La ou un plancher existait (ecran sans image), il l'emportait toujours sur
       le facteur : la bande ne cedait pas d'un pixel et l'enonce absorbait 100 %
       du deficit. Le facteur etait INERTE dans le cas meme qu'il visait.
     — La ou aucun plancher n'existait (ecran illustre, `hauteurMin` a 0), le
       facteur agissait pleinement et la bande tombait a 64 px — SOUS la hauteur
       de sa propre rangee de controles. Le bouton de validation se faisait alors
       rogner par un ancetre non defilable : inatteignable, meme a la molette.
   Une bande ne descend donc jamais sous ce qu'elle doit montrer, image ou pas :
   sa rangee de controles (50 px), ses marges hautes et basses (2 x 30), et de
   quoi apercevoir au moins une reponse.
   ⚠️ LES DEUX NOMBRES SONT MESURES, PAS CHOISIS. 238 px est la plus petite
   valeur qui laisse voir, a tous les paliers, soit une carte entiere de quiz
   (215 px suffisaient), soit les QUATRE libelles du curseur du questionnaire
   (238 px exactement au palier lg). 230 px, la premiere valeur posee, en
   manquait 8 — et deux configurations echouaient a 2 px pres.
   Et 54 %, pas 40 : le plafond proportionnel fait tomber le plancher sous sa
   valeur utile des que le conteneur descend sous 238/0,54 = 440 px de haut. A
   40 % il mordait des 575 px, c'est-a-dire qu'il protegeait d'un risque qui ne
   se presente qu'en dessous de 440 px tout en coutant la promesse entre les
   deux. L'enveloppe a ete mesuree taille par taille : le plancher maximal qui
   laisse encore le CTA dans le cadre vaut 496 px a 1280x700, 310 a 768x500,
   246 a 1280x450 — 238 reste dessous partout jusqu'a 440 px de hauteur, une
   valeur qu'aucune fenetre de navigateur reelle n'atteint sur un bureau.
   ⛔ MAIS UN TELEPHONE EN PAYSAGE L'ATTEINT, et 54 % s'y est revele faux. Mesure
   de l'enveloppe reelle aux tres petites hauteurs : 49,0 % a 1512x400, 49,6 % a
   667x375, et 47,2 % a 800x360 — le cas le plus contraignant. A 54 % le bouton
   de validation passait 11 a 24 px sous le bord, et sur le quiz AUCUN geste ne
   le rattrapait : il est rogne par un ancetre non defilable. La modale « passez
   en portrait » avertit mais ne bloque pas ; l'apprenant qui la ferme se
   retrouve devant un bouton inatteignable.
   ⚠️ ARBITRAGE, ET LA MESURE NE LE TRANCHE PAS. Sous ~450 px de hauteur de
   fenetre, les deux exigences sont geometriquement incompatibles : 238 px de
   bande pour montrer les quatre paliers du curseur, et le CTA dans le cadre.
   46 % passe partout pour le CTA ; en contrepartie les quatre libelles
   redemandent un defilement sous 517 px de haut. On choisit le bouton : un
   libelle qui demande un defilement reste atteignable, un bouton rogne par un
   ancetre non defilable ne l'est pas. */
const PLANCHER_BANDE = "min(238px, 46cqh)"

/* ⛔ 01/10/2026 — LE REPERE CLAVIER, EN BAS A GAUCHE DE LA BARRE D'ACTION.
   Point 3 d'Hicham : « tu aurais du me mettre la navigation avec les fleches
   sur tous les formats comme dans le design figma ».
   J'ai cherche dans le releve complet (6232 noeuds) : AUCUN noeud ne s'appelle
   fleche, chevron, precedent ni suivant. La seule navigation a fleche de toute
   la maquette est celle-ci — une touche DESSINEE, dont le glyphe est la fleche
   de retour chariot. Elle figure sur 51 planches sur 96.
   Et le code le savait : la ligne `justifyContent:"flex-end"` de la rangee de
   controles porte depuis le lot G le commentaire « les aides clavier ne sont pas
   dessinees tant que les gestionnaires n'existent pas — c'est l'ecart assume ».
   Assume par moi seul, jamais soumis a Hicham. Il est rattrape ici.
   ⚠️ C'EST AUSSI CE QUI REPOND AU POINT 5 (« format input text : c'est pas
   aligne »). La planche pose `justify-between` sur une rangee a DEUX bouts :
   le repere a gauche, le bouton a droite. Sans le repere, le bouton se retrouve
   seul et la barre parait bancale — exactement ce qu'on voit sur la capture.

   ⚠️ COTES MESUREES. Node 3:60551 (planche 02, un repere, 111 x 23 a x=381),
   node 4:589 (planche 10, deux reperes, 235 x 23 a x=751), node 3:262011
   (planche 07) et node 3:141180 (planche 06, « pour continuer », 126 de large).
   La touche elle-meme : node 3:60552, 37 x 23. Cotes :
     cabochon  37 x 23 pour le glyphe ↵ (11 + 15 + 11 = 37 a l'unite)
               41 x 23 pour « 1-4 »     (11 + 19 + 11 = 41 a l'unite)
     glyphe    15 x 9
     ecart cabochon -> libelle : 10 px, et libelle -> cabochon suivant : 10 px
               (428-418, 802-792, 922-912, 875-865 : quatre fois 10)
     rangee    111 de large pour un repere, 235 pour deux
   ⚠️ DEDUIT, PAS MESURE — le releve est une table de COTES, sans couleur ni
   effet. Le corps de 12 px vient du rapport hauteur/corps constant du fichier
   (nom du module : 18 px pour 12 de haut ; nom du chapitre : 14 px pour 9), qui
   donne 8 / 0,667 = 12. Le blanc, le filet gris et l'ombre portee sont lus sur
   la capture Figma d'Hicham du 01/10, pas sur une valeur exportee. A confirmer
   si le fichier finit par exposer ses jetons. */
/* ⚠️ Node 3:60552 pour le cabochon (37 x 23), node 4:573 pour la meme touche
   dessinee dans une carte de reponse du quiz.
   ⚠️ UN `<svg>` NU, PAS UN `Box component="svg"`, ET C'EST MESURE.
   `Box` traite `width` et `height` comme des proprietes de systeme MUI, donc
   comme du CSS — `width="15"` devient une longueur sans unite, invalide, et
   l'attribut SVG n'est jamais emis. Mesure au banc : cabochon large de 24 au
   lieu de 37 (11 + 2 + 11), le glyphe debordant de sa boite. */
const GLYPHE_ENTREE = (
  <svg width="15" height="9" viewBox="0 0 15 9" fill="none"
       aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
       style={{ display:"block", flexShrink:0 }}>
    <path d="M13.9 0.6V5.4H2.2" stroke="currentColor" strokeWidth="1.3" fill="none" />
    <path d="M5.9 1.7L1.5 5.4L5.9 9" stroke="currentColor" strokeWidth="1.3" fill="none" />
  </svg>
)

/* ⛔ 01/10/2026 — UN REPERE QUI NE MARCHE PAS EST UN MENSONGE A L'ECRAN.
   Dessiner « ↵ pour valider » sans brancher la touche serait pire que de ne
   rien dessiner : l'apprenant essaierait, et rien ne se passerait. Les deux
   arrivent donc ensemble, et c'est ce hook qui porte la moitie comportement.

   ⚠️ TROIS GARDE-FOUS, chacun paye par un piege connu :
   1. On n'ecoute QUE si le format est reellement joue : ni dans l'editeur, ni
      dans une vignette `onlyContent` (la fin de module rend un module entier en
      miniature — un raccourci y repondrait a la place de l'ecran visible).
   2. On ne detourne JAMAIS une frappe destinee a un champ de saisie. Sans ce
      test, taper « 3 » dans une reponse libre choisirait la proposition 3.
      C'est a l'appelant d'autoriser explicitement le cas du champ a une ligne,
      ou la touche Entree n'a pas d'autre emploi.
   3. Le gestionnaire passe par une reference : sans cela il change d'identite a
      chaque rendu, l'effet se reabonne a chaque rendu, et on accumule les
      ecouteurs.
   `typeof document` garde le rendu statique du banc, qui n'a pas de DOM vivant. */
export const saisieEnCours = (e) => {
  const n = e && e.target
  if (!n || !n.tagName) return false
  const t = String(n.tagName).toUpperCase()
  return t === "INPUT" || t === "TEXTAREA" || t === "SELECT" || n.isContentEditable === true
}

/* ⛔ 01/10/2026 — « COMMANDE » SUR MAC, « CTRL » AILLEURS.
   Lu une seule fois : la famille de clavier ne change pas en cours de session.
   ⚠️ LE BANC FIGE « Ctrl », et c'est normal : il rend dans jsdom, dont le
   `navigator` n'est pas celui d'un Mac. La reference du filet porte donc la
   variante non-Mac. Ce n'est pas un ecart a corriger — c'est la seule des deux
   que le banc puisse voir. */
export const TOUCHE_COMMANDE = (() => {
  if (typeof navigator === "undefined") return "Ctrl"
  const sig = `${navigator.platform || ""} ${navigator.userAgent || ""}`
  return /Mac|iPhone|iPad|iPod/.test(sig) ? "\u2318" : "Ctrl"
})()

/* `accepteModificateur` : par defaut le hook IGNORE toute frappe portant Cmd,
   Ctrl ou Alt — ces combinaisons appartiennent au navigateur (Cmd+R, Ctrl+F).
   Une seule situation fait exception, et elle est voulue : la zone de texte,
   ou Entree seule doit rester un retour a la ligne et ou c'est justement la
   COMBINAISON qui valide. */
export const useRaccourciClavier = (actif, surTouche, accepteModificateur) => {
  const garde = useRef(surTouche)
  garde.current = surTouche
  useEffect(() => {
    if (!actif) return undefined
    if (typeof document === "undefined" || typeof document.addEventListener !== "function") return undefined
    const ecouter = (e) => {
      /* Une combinaison (Cmd+R, Ctrl+F...) appartient au navigateur. */
      if (e.defaultPrevented) return
      if (!accepteModificateur && (e.altKey || e.ctrlKey || e.metaKey)) return
      const f = garde.current
      if (typeof f === "function") f(e)
    }
    document.addEventListener("keydown", ecouter)
    return () => document.removeEventListener("keydown", ecouter)
  }, [actif, accepteModificateur])
}

export const Touche = ({ children }) => (
  <Box sx={{
    display:"inline-flex", alignItems:"center", justifyContent:"center",
    /* ⚠️ 10 px DE MARGE INTERIEURE, PAS 11, ET LA MESURE TRANCHE. Le node donne
       un cabochon de 37 pour un glyphe de 15. 11 + 15 + 11 = 37 si la bordure
       ne compte pas ; 10 + 15 + 10 + 2 (une bordure de 1 px de chaque cote) = 37
       si elle compte. Au banc, la seconde lecture pose le cabochon a 37 ET le
       glyphe a x=392, soit les DEUX cotes du node ; la premiere ratait les deux
       de 2 px et de 1 px. */
    height:"23px", boxSizing:"border-box", padding:"0 10px", flexShrink:0,
    /* ⛔ 01/10/2026 (soir) — VALEURS RELEVEES, PLUS DEDUITES. Node 3:60552,
       lu par l'API Figma et non plus sur une capture :
         fond       #FFFFFF
         bordure    noir a 30 %, 1 px, alignee a l'INTERIEUR — d'ou `border-box`
         rayon      AUCUN. J'avais arrondi a 3 px en lisant une capture ; la
                    maquette ne porte aucun rayon.
         ombre      decalage 1/1, flou 0, ETENDUE 0,5, noir a 40 %. Mon relevé
                    a l'oeil disait 28 % et pas d'etendue.
         marges     10 px a gauche et a droite, 6 en haut et en bas
                    (10 + 15 + 10 + 2 bordures = 37 ; 6 + 9 + 6 + 2 = 23)
         ecart      10 px entre le cabochon et son libelle (`itemSpacing`)
       Les cotes, elles, etaient bonnes : 37 x 23, glyphe 15 x 9. */
    background:"#FFFFFF",
    border:"1px solid rgba(0,0,0,0.3)",
    borderRadius:0,
    boxShadow:"1px 1px 0 0.5px rgba(0,0,0,0.4)",
    /* ⚠️ 13 px DANS le cabochon, 12 px pour le libelle a cote : ce ne sont pas
       les memes noeuds ni la meme valeur. « 1-4 » est a 13 (node 4:591), « pour
       valider » a 12 (node 3:60554). J'avais mis 12 partout. */
    /* Gris #646362, pas noir : c'est la couleur du texte des touches ecrites
       (« 1-4 », node 4:591 ; et la touche « Ctrl » ajoutee a la maquette des
       zones de texte le 02/10/2026), la meme que leur libelle a cote.
       Vu par le comparateur le 02/10/2026. */
    fontSize:"13px", lineHeight:"1", color:"#646362",
    whiteSpace:"nowrap",
  }}>
    {children === "entree" ? GLYPHE_ENTREE : children}
  </Box>
)

/* `reperes` : [{ touche, libelle }]. `touche` vaut "entree", une chaine, ou un
   TABLEAU de plusieurs touches — « ⌘ » puis « ↵ » pour une combinaison, dessines
   cote a cote avec le meme ecart de 10 px que la planche met entre ses propres
   cabochons (node 4:589, deux reperes a 10 px).
   Masque sous le palier `md` : les planches Mobile ne le portent pas (releve :
   aucune des planches « Mobile » n'a de noeud « Alphabet » hors des reponses),
   et un repere clavier n'a pas de sens sur un ecran tactile. */
/* ⛔ 01/10/2026 — UN SEUL PREDICAT, PARTAGE AVEC `justifyContent`, ET C'EST
   PAYE PAR UNE MESURE. J'avais ecrit, dans le commentaire de la rangee de
   controles, que « sans repere, `space-between` et `flex-end` rendent pareil ».
   C'est FAUX : `space-between` avec UN SEUL enfant le pose a flex-START. Mesure
   au banc juste apres : le bouton Valider de `input-une-ligne` et de
   `texte-long` passait de x=1001 a x=381 — toute la barre basculait a gauche.
   Les deux endroits doivent donc poser la MEME question, d'ou ce predicat. */
export const aDesReperes = (reperes) => (reperes || []).some((r) => r && r.libelle)

/* ⛔ 01/10/2026 (nuit) — LE PREDICAT NE SUFFISAIT PAS : IL FALLAIT AUSSI LE PALIER.
   `aDesReperes` dit si le repere EXISTE ; il ne dit pas s'il est AFFICHE. Or il
   est cache sous `md` : la rangee recevait `space-between` avec un seul enfant
   VISIBLE, et le bouton Valider partait a gauche en Tablet et en Mobile — la ou
   les planches le posent a droite (3:60583 a x=662 sur 834, 3:60623 a x=243
   sur 393). Trouve par le comparateur maquette / rendu, pas par l'oeil.
   Une seule table decide donc des deux : l'affichage du repere, et la
   repartition de la rangee, palier par palier. */
/* ⛔ DECISION D'HICHAM DU 02/10/2026 (PD-15) : PAS DE REPERE CLAVIER EN TABLET,
   comme en Mobile — « ils n'ont pas de clavier ». Les planches Tablet de question
   le dessinent (02 : 3:60578), elles sont donc volontairement ecartees ici. Il
   avait ete affiche un temps en Tablet pour suivre la maquette (commit 4c3594f),
   puis retire sur photos. */
/* ⛔ DECISION D'HICHAM DU 03/10/2026 (PD-20) : EN MOBILE, LE BOUTON EST AU
   MILIEU, sur tous les formats — « au milieu sur le telephone je pense que
   c'est mieux ». Les planches Mobile le posent a droite (3:60623 a x=243 sur
   393) et pas toutes de la meme facon d'une section a l'autre : elles sont
   volontairement ecartees ici. Tablet et Desktop gardent la planche. La video
   et l'ecran titre centraient deja leur bouton en Mobile. */
export const REPERE_AFFICHE = [false, false, true, true]
export const repartitionRangee = (reperes) => REPERE_AFFICHE.map((vu, palier) => {
  if (palier === 0) return "center"
  return vu && aDesReperes(reperes) ? "space-between" : "flex-end"
})

export const RepereClavier = ({ reperes }) => {
  const utiles = (reperes || []).filter((r) => r && r.libelle)
  if (utiles.length === 0) return null
  return (
    <Box sx={{
      display: REPERE_AFFICHE.map((vu) => (vu ? "flex" : "none")),
      alignItems:"center", gap:"10px", flexShrink:0, minWidth:0,
      userSelect:"none",
    }}>
      {utiles.map((r, i) => (
        <Box key={i} sx={{ display:"flex", alignItems:"center", gap:"10px" }}>
          {(Array.isArray(r.touche) ? r.touche : [r.touche]).map((t, j) => (
            <Touche key={j}>{t}</Touche>
          ))}
          {/* ⛔ 12 px et #646362, RELEVES (node 3:60554). Le 12 etait deduit du
              rapport hauteur/corps et il tombait juste ; la couleur, elle, ne
              l'etait pas : `text.subTitle` vaut rgba(0,0,0,0.7), soit (75,74,74)
              sur le fond de la bande, la ou la maquette pose (100,99,98). */}
          <Box sx={{ fontSize:"12px", lineHeight:"1", color:"#646362", whiteSpace:"nowrap" }}>
            {r.libelle}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export const BandeReponse = ({
  hauteur = "400px", hauteurMin = "400px", largeur = LARGEUR_CARTES,
  largeurControles, fond = FOND_BANDE_BASSE, controles, onlyContent, calerEnBas, reperes, children,
  plancherMobile = PLANCHER_BANDE, hauteurFixeTablette = false, ecart = "20px", hautMobile = 20,
  debordeEmpile = false, plancherTablette = PLANCHER_BANDE, hautGrand = 30, sousConsigneMobile = false,
}) => (
  <Box sx={{
    background: fond,
    width:"100%",
    /* ⛔ 04/10/2026 — `debordeEmpile` : ECRAN ILLUSTRE EMPILE (Mobile, Tablet),
       LA BANDE VA D'UN BORD A L'AUTRE, comme sur les planches 04 et 05
       (3:80836 : 393 de large ; 3:100944 : 834). Elle vit dans la colonne, qui
       garde les gouttieres du conteneur (20 et 40 px) : elle les franchit. */
    ...(debordeEmpile ? {
      width:[`calc(100% + ${2 * GOUTTIERE_MOBILE}px)`, "calc(100% + 80px)", "100%", "100%"],
      marginLeft:[`-${GOUTTIERE_MOBILE}px`, "-40px", 0, 0],
      marginRight:[`-${GOUTTIERE_MOBILE}px`, "-40px", 0, 0],
      /* En fenetre courte (palier Tablet par la largeur, mais deux colonnes),
         rien a franchir : la bande sortait de 40 px sous la pastille et de 12 a
         25 px hors de l'ecran a droite (relecture du 04/10, 800 x 500). */
      [FENETRE_COURTE]: { width:"100%", marginLeft:0, marginRight:0 },
    } : {}),
    /* ⛔ 01/10/2026 — `calerEnBas` : LA BANDE SE COLLE AU BAS DE SA COLONNE.
       La planche illustree la pose de 582 a 982 sur une colonne de 75 a 982
       (node 3:100879, 821 x 400), soit collee au bas. Sans cela elle suivait
       simplement l'enonce et flottait au milieu — mesure a 382 au lieu de 582 —
       et l'enonce venait se coller contre elle, faute de place au-dessus.
       Uniquement aux deux grands paliers : aux paliers empiles, la bande suit le
       contenu et il n'y a pas de hauteur a remplir. */
    /* Empile et illustre (`debordeEmpile`) : 60 px sous la consigne en Mobile
       (3:80828, `pb-60`, la page defile ; 54,3 de marge, l'interligne non rogne
       de la consigne fait le reste, mesure au comparateur) ; collee au bas de l'ecran en Tablet
       (3:100944, y=974 a 1194). Une seule cle `marginTop`, qui choisit. */
    /* `sousConsigneMobile` : meme ecart de 60 px en Mobile pour le quiz illustre,
       dont la bande est deja pleine largeur (4:635, 4:348). */
    ...((calerEnBas || debordeEmpile || sousConsigneMobile) ? { marginTop:[(debordeEmpile || sousConsigneMobile) ? "54.3px" : null, debordeEmpile ? "auto" : null, calerEnBas ? "auto" : null, calerEnBas ? "auto" : null] } : {}),
    /* ⛔ ELLE CEDE, ELLE NE ROGNE PAS. Avec `flexShrink:0`, l'enonce et la bande
       etaient tous deux incompressibles : sur une fenetre courte, ou avec un
       enonce de trois lignes et six reponses, le bas de la bande passait sous
       l'ecran — bouton de validation compris — et AUCUNE barre de defilement
       n'apparaissait, l'ancetre rognant en silence. L'apprenant ne pouvait plus
       valider sa sequence. La bande se comprime donc, et c'est son bloc interne
       qui prend le relais en defilant ; la rangee de controles, elle, reste
       incompressible.
       ⚠️ FACTEUR 1, ET NON 2. J'avais mis 2 pour que la bande cede la premiere.
       Mesure : le facteur ne decide de rien la ou un plancher existe — il l'a
       toujours emporte, chiffres identiques avant et apres — et il coutait 22 a
       35 % de zone de reponses visible la ou le plancher ne mordait pas. Un
       reglage inerte quand il devrait agir et nuisible quand il ne devrait pas
       n'est pas un reglage : c'est le plancher qui fait le travail. */
    flexShrink:1,
    boxSizing:"border-box",
    display:"flex",
    flexDirection:"column",
    alignItems:"center",
    justifyContent:"space-between",
    /* 20 px aussi en Mobile : toutes les bandes Mobile des planches qui en ont
       une portent `itemSpacing` 20 (3:60619, 3:80756, 3:282137, 34:1318...). */
    /* `ecart` : le quiz pose 40 entre ses cartes et la rangee de controles
       (3:302259 et 3:382636), 20 en Mobile (3:382581). */
    gap: ecart,
    /* Gouttiere de la bande de reponse : 60 px des le palier Tablet, et
         GOUTTIERE_MOBILE au palier xs plutot qu'un 25 en dur. Releve par les
         deux agents, Vrai/Faux et feedback. */
      /* ⚠️ RELEVE PAR L'API (01/10/2026, nuit), sur toutes les bandes du fichier :
         Mobile 20 / 20 / 35 / 20 sans exception (3:60619, 3:80756, 3:141248,
         3:282137, 34:1318, 4:1400...) — le bas valait 20 dans le code ; Tablet
         30 en haut sur les planches de saisie et de quiz (3:60574, 3:80707,
         3:382635), le code posait 24. */
      /* `hautMobile` : le questionnaire reprend 13,5 px du haut pour les donner
         a son curseur vertical, dont les libelles debordent (04/10/2026). */
      /* `hautGrand` : le Vrai/Faux pose ses cartes a 40 px du haut de la bande
         en Desktop et Tablet (6:309, 34:1298), les autres ecrans a 30. */
      /* (un nombre, ou [Tablet, md, Desktop] : le Vrai/Faux illustre garde 30 en
         Tablet, 4:412) */
      padding:[`${hautMobile}px ${GOUTTIERE_MOBILE}px 35px`, ...[0, 1, 2].map((i) => `${[].concat(hautGrand)[i] ?? [].concat(hautGrand)[0]}px 60px 30px`)],
    /* ⛔ LA HAUTEUR EST PLAFONNEE, PAS FIGEE, ET C'EST MESURE.
       Figma ecrit `h-[444px]` sur une planche de 982 px de haut. Pose telle
       quelle, cette hauteur ROGNE la bande des que la fenetre est plus courte :
       banc Playwright, la bande passait sous le bas de l'ecran a 1024x640 et a
       1512x600, controles compris — l'apprenant ne voyait plus le bouton.
       On garde donc la valeur de la planche comme PLAFOND, et on la rapporte a
       la hauteur du conteneur en dessous. `cqh` mesure la racine du format, qui
       declare `containerType:"size"` : la regle vaut donc aussi dans l'apercu
       admin, rendu dans une boite fixe de 1500 x 1000.
       Mesures au banc : 444 px a 1512x982 et a 1920x1080 (la planche, au pixel),
       363 px a 1440x790, 276 px a 1512x600 — et plus aucun rognage, le bloc de
       reponses prenant le relais en defilant. */
    /* ⛔ 01/10/2026 (nuit) — LA TABLETTE SUIT LE DESKTOP. Les planches Tablet de
       texte sans image posent la bande a 400 px, collee en bas (3:60574,
       3:80707, 3:282078, 34:1298 : y=794, h=400 sur 1194). Le code la laissait
       a la hauteur de son contenu sous `md` : 238 px en Tablet sur l'ecran de
       saisie. Seul le Mobile epouse son contenu (175, 270, 340... selon l'ecran).
       ⚠️ SUR DEMANDE DU FORMAT, PAS PAR DEFAUT. Applique a tous, le plafond
       cachait en Tablet les reponses 5 et 6 d'un quiz a six reponses longues,
       visibles avant (photos avant / apres du 02/10). Les planches de quiz
       Tablet font d'ailleurs 444 a 553 selon le contenu (3:382635, 33:447).
       Seuls la saisie et le questionnaire, dont le contenu est de hauteur
       fixe, le demandent. */
    height:["auto", hauteurFixeTablette ? hplafond(hauteur) : "auto", hplafond(hauteur), hplafond(hauteur)],
    /* Le plancher s'applique a TOUS les paliers : le defaut mesure sur le
       questionnaire (curseur reduit a 29 px, aucun palier visible) se produisait
       a 768x500, donc sous `md`, la ou `hauteurMin` valait encore 0. */
    /* xs : le format peut passer un plancher plus bas que le plancher commun,
       quand son contenu est fixe et plus petit (saisie : 175 px, 3:60619) —
       sinon la bande Mobile mesurait 238 pour 175 de contenu. */
    minHeight:[
      plancherMobile,
      hauteurFixeTablette ? `max(${hplafond(hauteurMin, 42)}, ${PLANCHER_BANDE})` : plancherTablette,
      `max(${hplafond(hauteurMin, 42)}, ${PLANCHER_BANDE})`,
      `max(${hplafond(hauteurMin, 42)}, ${PLANCHER_BANDE})`,
    ],
    maxHeight:"100%",
  }}>
    <Box sx={{
      position:"relative",
      /* ⛔ LA RESERVE DE LA BARRE DE DEFILEMENT DEBORDE A DROITE (01/10/2026,
         nuit). Ce bloc defile, et BLOC_DEFILANT lui reserve 4 px a droite : ils
         etaient pris SUR le contenu, d'ou un champ et des cartes de 746 px au
         lieu des 750 de la planche (3:60548), et 349 au lieu de 353 en Mobile
         (3:60620). Le bloc s'elargit donc de sa reserve, et une marge negative
         la fait passer dans la marge de la bande : la boite visible garde
         exactement la largeur de la colonne, et reste centree. */
      width:`calc(100% + ${BLOC_DEFILANT.paddingRight})`,
      maxWidth:`calc(${largeur} + ${BLOC_DEFILANT.paddingRight})`,
      marginRight:`-${BLOC_DEFILANT.paddingRight}`,
      flex:1,
      minHeight:0,
      display:"flex",
      flexDirection:"column",
      /* ⛔ ANCRE EN HAUT, PAS CENTRE. Les planches posent `justify-between` sur
         la bande : le bloc de reponses colle au haut (padding 30) et la rangee
         de controles au bas. Centre, il flottait au milieu d'un grand vide des
         qu'il y avait peu de reponses — visible sur LAB avec deux cartes, ou
         118 px separaient le haut de la bande de la premiere carte contre 30 px
         sur la planche. `safe` reste : il ancre en haut plutot que de creer du
         vide inatteignable quand ca deborde. */
      justifyContent:"safe flex-start",
      /* ⛔ LE DEFILEMENT EST FOURNI ICI, PAS ESPERE DE L'APPELANT.
         La bande est compressible ; sans defilement interne, comprimer revient a
         rogner. Le quiz s'en sortait parce que son enfant declare lui-meme un
         bloc defilant — pas le questionnaire, dont l'enfant est un curseur nu :
         mesure a 768x500, la bande tombait a 99 px, le rail traversait le bouton
         et les quatre libelles de paliers sortaient de l'ecran. L'apprenant
         devait placer un curseur dont il ne voyait aucun palier.
         Une promesse tenue par un seul appelant sur deux n'est pas une
         promesse : elle appartient au composant. */
      ...BLOC_DEFILANT,
      overflowY:"auto",
      overflowX:"hidden",
    }}>
      {children}
    </Box>
    {/* ⚠️ Pas de test sur `controles` : c'est un fragment JSX, donc toujours
        vrai. C'est `onlyContent` qui decide, comme le faisait `BottomBar`. */}
    {
      <Box sx={{
        display: onlyContent ? "none" : "flex",
        width:"100%",
        /* ⛔ LA RANGEE DE CONTROLES PEUT ETRE PLUS ETROITE QUE LE CONTENU, et la
           planche le dit : sur le questionnaire (node 3:261993), la piste et ses
           libelles occupent 892 px quand la rangee de controles en fait 750.
           Confondre les deux tronquait les libelles des deux paliers extremes —
           « jamais » et « toujours », ceux qui donnent son sens a l'echelle. */
        maxWidth: largeurControles || largeur,
        flexShrink:0,
        alignItems:"center",
        /* ⛔ 01/10/2026 — `space-between`, ET LA RANGEE A ENFIN DEUX BOUTS.
           Figma pose `justify-between` : repere clavier a gauche, CTA a droite.
           Cette ligne a porte `flex-end` depuis le lot G, avec pour motif que
           « les aides clavier ne sont pas dessinees tant que les gestionnaires
           n'existent pas ». Les deux arrivent ensemble, donc elle redevient ce
           que la planche dit — MAIS seulement quand il y a bien deux bouts.
           Voir `repartitionRangee` : `space-between` avec un seul enfant VISIBLE
           le colle a GAUCHE, et c'est mesure — y compris quand le repere existe
           mais est cache par le palier (02/10/2026). */
        justifyContent: repartitionRangee(reperes),
      }}>
        <RepereClavier reperes={reperes} />
        {controles}
      </Box>}
  </Box>
)

/* `blanche` (04/10/2026) : la barre BLANCHE des ecrans de bilan, planches 14
   et 15 — rectangle blanc de 80 px a filet #E7E7E7 et ombre vers le haut
   (4:1580, 4:1581), bouton centre en hauteur a 25 px du bord droit (4:1582 :
   1337 + 150 = 1487 sur 1512) ; en Mobile 20 / 35 px de marge haute / basse et
   bouton au milieu (4:1825, PD-20). Le Diagnostic et la fin de module
   l'emploient. */
export const BottomBar = ({ buttons, onlyContent, fond = "transparent", hauteur, colonne, reperes, demiDroite, bas, blanche }) => {
  return (
    <Box sx={{
      display:onlyContent?"none":"flex",
      /* ⛔ 01/10/2026 — `demiDroite` : LA BARRE VIT DANS LA MOITIE DE CONTENU.
         Point 1 d'Hicham (« format texte : bouton suivant c'est pas bon »).
         Sur un ecran ILLUSTRE, la barre basse etait rendue sur toute la plaque :
         mesure a 1512 sur `texte-long-image`, bande de 0 a 1512 et bouton a
         x=1001 — c'est-a-dire a cheval sur la moitie de l'IMAGE, pendant que le
         texte qu'il conclut vit de 731 a 1472. L'oeil voit un bouton qui
         n'appartient a rien.
         La planche 05, elle, pose la rangee d'action de l'ecran illustre a
         751 -> 1452 avec le CTA a 1322 (node 3:121026) — et c'est deja ou
         tombent le quiz et le champ de saisie, qui passent par `BandeReponse`.
         Un seul format restait dehors ; il rentre.
         ⚠️ Aux paliers EMPILES, il n'y a pas de moities : la barre reprend toute
         la largeur, sinon elle se retrecirait sous une image pleine largeur. */
      ...(demiDroite ? {
        marginLeft:[0, 0, MOITIE_IMAGE, MOITIE_IMAGE],
        width:["100%", "100%", MOITIE_CONTENU, MOITIE_CONTENU],
        boxSizing:"border-box",
      } : {}),
      justifyContent: colonne ? "center" : "flex-end",
      alignItems:"flex-end",
      background:fond,
      /* 80 px : `Rectangle 142940` a y=902 sur une plaque de 982, donc 80 de haut
       (nodes 4:1581 et 4:1036, planches 14 et 15). */
    minHeight: hauteur || "80px",
      /* ⚠️ MEMES GOUTTIERES QUE LE CONTENU (21/09/2026). La rangee basse
         porte le node Frame 1261156282 : 353 en Mobile (donc 20 de gouttiere) et
         750 en Tablet. En Tablet le plafond LARGEUR_CARTES de 750 mordait deja
         et rendait la bonne valeur ; en Mobile rien ne mordait, et la barre
         faisait 343 au lieu de 353. Elle doit s'aligner sur le contenu, sinon le
         CTA ne tombe plus sur le bord de la colonne. */
      /* `bas` : la marge basse palier par palier, pour un format dont la
         planche la donne (texte long : 35 en Mobile, 30 en Tablet — 3:141248,
         3:141213). Sans elle, les valeurs historiques. */
      padding:[`0px ${GOUTTIERE_MOBILE}px ${enLongueur(bas?.[0], 20)}`, `0px 42px ${enLongueur(bas?.[1], 24)}`, `0px 60px ${enLongueur(bas?.[2], 30)}`, `0px 60px ${enLongueur(bas?.[3], 30)}`],
      boxSizing:"border-box",
      /* EN DERNIER : remplace fond, hauteur, alignement et marges ci-dessus */
      ...(blanche ? {
        background:"#FFFFFF",
        /* filet en HAUT seulement (4:1825 : `top 1`) ; l'ombre vers le haut en
           Tablet et Desktop (4:1580, 4:1754), aucune en Mobile (relecture du
           04/10 : la planche Mobile n'en a pas) */
        borderTop:"1px solid #E7E7E7",
        boxShadow:["none", "0px -2px 20px rgba(0,0,0,0.05)", "0px -2px 20px rgba(0,0,0,0.05)", "0px -2px 20px rgba(0,0,0,0.05)"],
        alignItems:"center",
        flexShrink:0,
        minHeight:["auto", "80px", "80px", "80px"],
        padding:[`20px ${GOUTTIERE_MOBILE}px 35px`, "0px 25px", "0px 25px", "0px 25px"],
      } : {}),
    }}>
      {/* `colonne` cale le bouton sur le bord droit d'une colonne centree, au
          lieu du bord de l'ecran. Ce n'est PAS un choix esthetique : sur la
          planche du quiz, le bouton s'aligne sur la colonne des cartes. Le
          format qui le demande donne la largeur qu'il a mesuree. */}
      {/* ⛔ 01/10/2026 — MEME RANGEE A DEUX BOUTS QUE `BandeReponse`. La planche
          06 (texte long, sans bande) pose « ↵ pour continuer » a x=381 et le CTA
          a x=981, dans la meme barre de 750 (node 1261156282).
          Et le meme garde-fou : sans repere on reste en `flex-end` (au milieu
          en Mobile, PD-20), sinon le bouton seul basculerait a gauche (mesure,
          cf. `repartitionRangee`). */}
      <Box sx={{ width:"100%", maxWidth: colonne || "none", display:"flex",
                 justifyContent: repartitionRangee(reperes),
                 alignItems:"center" }}>
        <RepereClavier reperes={reperes} />
        {buttons}
      </Box>
    </Box>
  )
}

export const TopBar = ({ actFormation, actIndex, setViewPlanCours, viewPlanCours, onlyContent }) => {
  const navigate = useNavigate();

  const {
    updateUserContext,
  } = useContext(UserContext)
  const {
    actLanguage,
    languageValues,
  } = useContext(LanguageContext)

  const theme = useTheme();
  const mediaQueryUpMd = useMediaQuery(theme.breakpoints.up('md'));

  const [chaps, setChaps] = useState([undefined, undefined, undefined])

  // PD-4 (décision produit validée) : un candidat THE SCAN suit l'évaluation
  // dans l'ordre prévu ; on NE lui donne PAS l'accès à PlanCours (structure
  // complète du parcours). On garde le repère de chapitre comme simple label,
  // sans affordance d'ouverture. Aucun changement pour l'apprenant THE LAB.
  const estCandidatScan = getCurrentUser()?.type === "theScanCandidat"

  useEffect(() => {
    if (actFormation && actIndex) {
      let actChapitre = actFormation?.chapitre[actIndex?.chapitre]
      let prevChapitre = actFormation?.chapitre[actIndex?.chapitre-1]
      let nextChapitre = actFormation?.chapitre[actIndex?.chapitre+1]
      setChaps([prevChapitre, actChapitre, nextChapitre])
    }
  }, [actFormation, actIndex])



  // L76 : la croix ferme le cours au lieu d'ouvrir le sommaire, que les
  // pastilles de chapitre ouvrent deja.
  //
  // Lot C (18/09) : sur /icebreaker aussi, la croix ramene au dashboard, SANS
  // deconnexion. La garde App.js n'enferme plus dans l'icebreaker (elle ne
  // redirige que sur /formation tant que isDone === false), donc revenir au
  // dashboard ne rebondit plus. Quitter != terminer : isDone reste inchange.
  const quitterLeCours = () => {
    const utilisateur = getCurrentUser()
    if (utilisateur?.type === "theScanCandidat") {
      window.location.href = GLB_URL_THESCAN   // le candidat n'a pas de dashboard
      return
    }
    updateUserContext() // L24 : contexte a jour avant le retour
    navigate("/dashboard")
  }


  return (
    <>
      <Box sx={{
        background:["white", "transparent"],
        /* Lot G : header a 75 px sur les frames Desktop (releve sur 3:60522). */
        /* Bandeau : 59 px en Mobile (`Rectangle 142940`, 15 occurrences), 75 aux
           deux grands paliers (30 et 26 occurrences). */
        /* ⚠️ LE FILET EST DANS LE BANDEAU. Figma pose `Rectangle 142940` sur
           75 px et y place le filet a y=73 : les deux derniers pixels du
           bandeau, pas une rangee supplementaire. Le code les empilait, d'ou un
           en-tete de 87 px au lieu de 75 — 12 des 22 px de decalage mesures sur
           la pastille. On retire donc les 2 px du bandeau pour que
           bandeau + filet fasse exactement la hauteur de la planche.
           En Mobile la planche place bien le filet SOUS la bande (bande 59..118,
           trait a 119), donc le 59 reste entier. */
        height:["59px", "73px", "73px", "73px"],
        /* ⚠️ Le `minHeight` l'emportait sur la hauteur et remettait 75 px :
           il suit desormais la meme regle, bandeau + filet = 75. */
        minHeight:["57px", "73px", "73px", "73px"],
        /* Gouttiere du bandeau : le logo est a x=31 en Desktop (3:20282), x=27
           en Tablet (3:40430) et x=22 en Mobile (3:40460). Le code posait 25
           partout. */
        /* ⚠️ LE CENTRE VERTICAL DU BANDEAU N'EST PAS CELUI DE SA BOITE (01/10/2026,
           nuit). Desktop et Tablet : la planche centre logo et textes sur
           y=37,5 (logo 3:60529 de 25 a 49,8 ; capitales du chapitre 3:60526 de
           33 a 42), soit le centre des 75 px — filet compris. La boite du code
           fait 73 : son centre tombait a 36,5. 2 px en haut le remontent a 37,5.
           Mobile : bande 59 px, mais logo, colonne et croix sont centres sur
           y=27,5 (croix 3:60638 de 10 a 45 apres la barre d'etat) : 4 px en bas.
           Constate par le comparateur maquette / rendu, palier par palier. */
        padding:["0px 22px 4px", "2px 27px 0px", "2px 31px 0px", "2px 31px 0px"],
        display:onlyContent?"none":"flex",
        alignItems:"center",
        justifyContent:"space-between",
        gap:["10px", "20px"],
      }}>
        {/* ⛔ LES DEUX COTES DOIVENT PESER PAREIL (21/09/2026).
            La rangee est en `space-between` et le titre etait en `flex:1` : il
            occupait donc l'espace QUI RESTE, et son centre suivait la largeur des
            cotes. Tant que la gauche ne portait que le logo (~26 px) face a la
            croix (~24 px), l'ecart ne se voyait pas. Le NOM DU MODULE, ajoute le
            19/09, a porte la gauche a ~170 px : le titre partait de +73 px vers la
            droite. Mesure sur la capture d'Hicham du 21/09 a 16h33 : +79 px.
            ⚠️ Une premiere tentative sortait le titre du flux (`position:absolute`).
            Pire : le `space-between` redistribuait alors les autres et le nom du
            module partait au centre. On ne sort personne du flux — on donne aux
            deux cotes la MEME largeur, et le centre redevient celui de la page. */}
        {/* ⚠️ 10 px ENTRE LOGO, SEPARATEUR ET NOM DU MODULE, A TOUS LES PALIERS
            (01/10/2026, nuit). Le cadre 3:60527 porte `itemSpacing: 10` ; le code
            posait 20 des le palier sm : separateur et nom du module 10 px trop a
            droite, mesures par le comparateur maquette / rendu. */}
        <Box sx={{ flex:"1 1 0", display:"flex", alignItems:"center", gap:"10px", minWidth:0 }}>
        <Box
          sx={{
            /* ⚠️ 26 px AUX TROIS PALIERS (relecture complete, 22/09) : le node
               `Group 1171276344` mesure 26 x 25 en Desktop, Tablet ET Mobile,
               sur 30, 26 et 27 occurrences. Le 23 du palier xs etait invente. */
            width:"26px",
            minWidth:["23px", "26px"],
            cursor:"pointer",
          }}
          onClick={() => {
            updateUserContext()
            navigate("/dashboard")
          }}
        >
          {/* `display:block` : une image EN LIGNE s'assoit sur la ligne de base
              et laisse dessous le jour des descendantes. La boite du logo
              mesurait 27 de haut pour une image de 24, et le centrage vertical
              du bandeau portait sur les 27 : logo 2 px trop haut (3:60529). */}
          <img
            src={smallLogo}
            style={{width:"100%", display:"block"}}
          />
        </Box>
        {/* NOM DU MODULE — node 3:261977, a gauche, apres le logo et un
            separateur (node 3:261976).
            ⚠️ « Nom du module » est un PLACEHOLDER DE DONNEE dans la planche,
            pas un texte a afficher tel quel : la planche indique « ici vient le
            vrai nom du module ». On rend donc `actFormation.name`, la donnee
            canonique. Voir METHODE.md, « Texte Figma : identifier son role ».
            Le meme motif — petit logo, filet, libelle — existe deja dans le
            Plan de formation : on ne reinvente rien.
            Ne contredit PAS PD-1, qui retire les TROIS PASTILLES de chapitre et
            ne dit rien du nom du module. Masque au candidat THE SCAN, comme le
            reste de ce qui nomme la formation (regle S8). */}
        {!estCandidatScan && actFormation?.name &&
          <>
            <Box sx={{
              width:"1px", height:["18px", "22px"], flexShrink:0,
              backgroundColor:"rgba(10,10,11,0.15)",
              /* Tablet compris : voir la note du nom du module ci-dessous. */
              display:["none", "block", "block", "block"],
            }} />
            <Box sx={{
              /* ⚠️ NOM DU MODULE RELU DANS FIGMA (21/09/2026, lot 3b).
                 18 px, jeton « Text/Desktop/Label/Medium », noeud 3:20286.
                 Le noeud Tablet 3:40435 porte la MEME chaine sur la MEME largeur
                 rendue (128 px) que le Desktop : meme corps, donc. Le palier xs
                 passe de 13 a 16 px. DEDUIT, et voici le calcul : les noeuds
                 portent `text-box-trim`, donc la hauteur rendue suit le corps.
                 Le nom du module mesure 12 px de haut a Desktop et Tablet pour
                 un corps de 18 (3:20286, 3:40435) et 11 px en Mobile (3:161358),
                 soit 11/12 x 18 = 16,5 px. Les hauteurs etant donnees a l'unite,
                 la fourchette est de 16,5 +/- 0,8 : on retient 16, la valeur de
                 l'echelle conventionnelle 14/16/18 qui tombe dedans.
                 ⚠️ DEDUIT, pas mesure : a confirmer si la planche Mobile finit
                 par exposer son jeton.
                 Interligne 26 px : porte par les SIX noeuds de texte de
                 l'en-tete, et jamais declare dans le code, qui retombait sur le
                 `normal` du navigateur (environ 17 a 22 px). */
              fontSize:["16px", "18px", "18px", "18px"],
              lineHeight:"26px",
              color:"text.black",
              whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
              /* ⛔ DECISION D'HICHAM DU 01/10/2026 : « suis la maquette ».
                 Le nom du module etait masque sous `md` (« l'en-tete n'a pas la
                 place de porter les deux libelles ») ; les planches le montrent :
                 Tablet a gauche apres le separateur (3:60602), Mobile au CENTRE,
                 au-dessus du chapitre (3:60636). Ici, la place de gauche, des sm ;
                 en xs il est rendu dans la colonne centrale, plus bas. */
              display:["none", "block", "block", "block"],
              maxWidth:["0px", "260px", "260px", "320px"],
              flexShrink:1, minWidth:0,
            }}>
              {actFormation.name}
            </Box>
          </>
        }
        </Box>
        {/* CHROME REDESIGN VALENTIN 2026 — header minimalise (decision Hicham,
            16/09/2026). Les 3 pastilles prev/actif/suiv sont RETIREES au profit
            d'un SEUL reperage de chapitre, coherent avec le nouveau header
            epure. Le plan complet reste accessible via PlanCours, donc supprimer
            prev/suiv ne perd aucune information de navigation.
            ⛔ PRODUCT DECISION : Figma ne definit pas l'interaction d'acces au
            plan. On PRESERVE la fonctionnalite existante (ouvrir le PlanCours),
            portee par ce seul element, avec chevron + infobulle + survol pour
            qu'elle reste DECOUVRABLE (cf. correctif L76 : « une fonction qu'on
            ne trouve pas n'existe pas »). Aucune navigation complexe reintroduite. */}
        <Box sx={{
          flex:"0 0 auto",   /* voir la note du bloc GAUCHE ci-dessus : le titre ne prend plus l'espace restant */
          display:"flex",
          /* Mobile : une COLONNE, nom du module au-dessus du chapitre (cadre
             3:60635, 195 px, pile verticale). Calee PAR LE HAUT et non centree :
             centree, la position des capitales du module ne depend pas de
             l'interligne (calcul fait) et tombait 1,6 px trop haut. La planche
             pose les capitales du module a y=14 et du chapitre a y=33 (3:60636,
             3:60637, barre d'etat retiree) : 11,5 px de marge et 3,5 entre les
             deux lignes les y mettent. */
          flexDirection:["column", "row", "row", "row"],
          alignItems:"center",
          /* ⚠️ CALEE EN HAUT SEULEMENT QUAND IL Y A DEUX LIGNES (relecture du
             02/10/2026). Avec une seule ligne — candidat THE SCAN, dont le nom
             du module est masque, ou icebreaker, sans chapitre — la caler en
             haut posait le texte 8 px au-dessus du logo et de la croix : on la
             centre alors, comme eux. */
          ...(!estCandidatScan && actFormation?.name && chaps?.[1]
            ? { alignSelf:["flex-start", "auto"], marginTop:["11.5px", 0] }
            : {}),
          gap:["3.5px", 0],
          justifyContent:"center",
          userSelect:"none",
          overflow:"hidden",
        }}>
          {!estCandidatScan && actFormation?.name &&
            <Box sx={{
              display:["block", "none", "none", "none"],
              fontSize:"16px", lineHeight:"16px", color:"text.black",
              maxWidth:"195px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
              textAlign:"center",
            }}>
              {actFormation.name}
            </Box>
          }
          {chaps?.[1] &&
            <Box
              onClick={estCandidatScan ? undefined : () => {
                setViewPlanCours(true)
              }}
              title={estCandidatScan ? undefined : (languageValues?.formation?.voirLePlan?.[actLanguage] || "Voir le plan du module")}
              sx={{
                display:"flex",
                /* ⚠️ PASTILLE RELUE DANS FIGMA (22/09). Le cadre fait 172,235 x 31 en
               Desktop et Tablet (69 occurrences), et TOUTES ses cotes se
               recomposent a l'unite :
                 hauteur  8 + 15 (icone) + 8 = 31
                 largeur  8 + 13,235 (icone) + 6 (gap) + 137 (texte) + 8 = 172,235
               Deux equations independantes qui tombent juste : ce ne sont pas
               des approximations. */
            gap:"6px",
                alignItems:"center",
                maxWidth:"100%",
                /* xs : pas de marge verticale, la colonne module + chapitre
                   doit tenir dans les 55 px utiles de la bande Mobile. */
                /* ⚠️ xs : la boite visible n'a pas de marge verticale, mais la
                   zone qu'on touche du doigt en a une — 6 px en haut et en bas,
                   rendus par une marge negative egale. Sans elles, le seul acces
                   au plan du module sur telephone ne faisait que 15 px de haut
                   (relecture du 02/10/2026) ; la croix, elle, en fait 35. */
                padding:["6px 8px", "8px 10px"],
                margin:["-6px 0", 0],
                overflow:"hidden",
                cursor:estCandidatScan ? "default" : "pointer",
                transition:"background .15s ease",
                ...(estCandidatScan ? {} : { "&:hover": { background:"rgba(13,13,13,0.06)" } }),
              }}
            >
              {/* ⚠️ L'ICONE NE GROSSIT PAS AVEC LE PALIER : 13,235 x 15 a l'identique
              en Desktop et en Mobile. Le code montait a 16 px de large, donc
              17,88 de haut avec le ratio 17:19 du fichier SVG — a lui seul cet
              excedent faisait deja deborder le cadre de 31 px.
              La hauteur est posee EXPLICITEMENT, sinon le ratio du fichier la
              recalcule et rate le 15. */}
          <Box sx={{width:"13.235px", minWidth:"13.235px", height:"15px"}}>
                <img src={moduleMiniIcon} style={{width:"100%"}} />
              </Box>
              <Box sx={{
                /* ⚠️ NOM DU CHAPITRE RELU DANS FIGMA (21/09/2026, lot 3b).
                   14 px et non 18, jeton « Text/Desktop/Label/Small », noeuds
                   3:20281 (Desktop) et 3:40429 (Tablet). Le chapitre est un
                   LIBELLE, pas un titre : sur la planche il est plus petit que
                   le nom du module (18), le code faisait l'inverse.
                   Mobile : 13 px. ⛔ CORRIGE LE 01/10/2026 (nuit). Ce commentaire disait
                   « 14 px AUSSI, et ce n'est pas une deduction » : c'en etait une,
                   tiree d'une hauteur de 9 px commune aux trois paliers. Or Figma
                   ARRONDIT A L'ENTIER la hauteur d'un texte rogne, et 13 comme 14 px
                   donnent 9. L'API, interrogee sur le noeud meme qu'il citait,
                   repond 13 (3:161359 ; 3:60637 sur la planche 02 aussi).
                   L'interligne de 15 px en xs suit la pastille : la colonne Mobile
                   module + chapitre doit tenir dans 55 px. */
                fontSize:["13px", "14px", "14px", "14px"],
                lineHeight:["15px", "26px", "26px", "26px"],
                /* ⚠️ Noir a 60 %, pas opaque. Les TROIS noeuds le portent :
                   3:20281 (Desktop), 3:40429 (Tablet), 3:161359 (Mobile).
                   En opaque, le chapitre pesait autant que le nom du module et
                   perdait son statut de libelle secondaire. */
                color:"rgba(0, 0, 0, 0.6)",
                whiteSpace:"noWrap",
                overflow:"hidden",
                textOverflow:"ellipsis"
              }}>
                {chaps[1]?.title}
              </Box>
              {/* Le chevron dit « ceci s'ouvre » — seul signe d'affordance du
                  header epure. PD-4 : pas de chevron pour le candidat THE SCAN,
                  puisqu'il n'y a rien à ouvrir. */}
              {!estCandidatScan &&
                <Box component="span" sx={{
                  display:"flex", alignItems:"center", flexShrink:0,
                  marginLeft:["2px","4px"], color:"text.black", opacity:0.55,
                }}>
                  <svg width="9" height="6" viewBox="0 0 9 6" aria-hidden="true">
                    <path d="M1 1L4.5 4.5L8 1" stroke="currentColor" strokeWidth="1.4" fill="none"/>
                  </svg>
                </Box>
              }
            </Box>
          }
        </Box>
        {/* L12 : croix de fermeture (le picto logout restait ambigu).
            Lot C (18/09) : meme sur /icebreaker, la croix ramene au dashboard
            via quitterLeCours (plus de modale de deconnexion). */}
        {/* Bloc DROIT, de meme poids que le bloc GAUCHE : c'est ce qui rend le
            centre du titre egal au centre de la PAGE. */}
        <Box sx={{ flex:"1 1 0", display:"flex", justifyContent:"flex-end", alignItems:"center", minWidth:0 }}>
        {/* ⚠️ LA CIBLE TACTILE VAUT 40 px, PAS 20 (relecture complete, 22/09).
            Figma distingue deux boites : le CADRE `Frame 1261156277`, 40 x 40 a
            Desktop et Tablet, 35 x 35 en Mobile (30 occurrences, 26, 27), et le
            GLYPHE a l'interieur, 15 px (12 en Mobile). Le code n'avait que le
            glyphe, a 20 px : la surface cliquable etait quatre fois trop petite,
            et sous le minimum recommande pour le doigt.
            Le glyphe passe donc a sa vraie taille et recoit un cadre centre. */}
        <Box
          onClick={quitterLeCours}
          sx={{
            width:["35px", "40px"],
            height:["35px", "40px"],
            minWidth:["35px", "40px"],
            /* Desktop : le cadre de la croix est a y=19 sur toutes les planches
               (3:302244, 3:261978, 3:60532...), 1,5 px plus bas que le reste
               de l'en-tete centre ; Tablet et Mobile le centrent (3:282121). */
            marginTop:[0, 0, "3px", "3px"],
            display:"flex",
            alignItems:"center",
            justifyContent:"center",
            cursor:"pointer",
          }}
        >
          <Box
            component="img"
            src={closeIcon}
            sx={{ width:["12px", "15px"], height:["12px", "15px"], display:"block" }}
          />
        </Box>
        </Box>
      </Box>
      {/* Lot A (18/09) : barre de progression de sequence SOUS le header,
          conforme aux frames NEW (la progression quitte l'ancien bandeau bas).
          Piste claire + remplissage noir anime. Masquee en mode onlyContent.
          ⛔ TopBar est un composant PARTAGE : IcebreakerQuestions l'utilise sans
          contexte de sequence (pas d'actIndex). On ne rend la barre QUE si le
          contexte de progression est present (actFormation ET actIndex), sinon
          un filet a 0% fuirait sur des ecrans sans notion de sequence. */}
      {actFormation && actIndex &&
        /* Lot G — valeurs relevees sur les nodes Figma (identiques sur 02, 08...) :
           la barre n'est PAS pleine largeur, elle fait 1450 px centres sur 1512 ;
           hauteur 2 px ; remplissage #0A0A0B sur une piste du meme noir a 10 %. */
        <Box sx={{
          display:onlyContent?"none":"flex",
          justifyContent:"center",
          /* ⚠️ DES MARGES, PAS UN PLAFOND (21/09/2026). Hicham : « sur un
             grand ecran le fil d'Ariane n'est pas complet ». La barre portait
             `maxWidth: 1450px`, un plafond FIXE : juste a 1512, ou 1512 - 62
             fait bien 1450, mais fige a 1450 sur un 1920, laissant 235 px de
             vide de chaque cote alors que le logo et la croix vont, eux,
             jusqu'aux bords.
             La planche ne dit pas « 1450 », elle dit « 31 px de marge » :
             Desktop 1450 sur 1512 (node 3:20278), Tablet 780 sur 834 (3:40426,
             soit 27 px), Mobile pleine largeur (3:40471, x=0 sur 393).
             Meme motif que la gouttiere : une valeur absolue relevee sur UNE
             planche, prise pour une regle. */
          padding:["0px", "0px 27px", "0px 31px", "0px 31px"],
        }}>
          <Box sx={{
            width:"100%",
            height:"2px",
            background:"rgba(10,10,11,0.1)",
          }}>
            <Box sx={{
              width: progressionSequence(actFormation, actIndex) + "%",
              height:"100%",
              background:"#0A0A0B",
              transition:"width .4s ease-in-out",
            }} />
          </Box>
        </Box>
      }
    </>
  )
}
